import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import { showToast } from '../components/Toast';
import FunnelWaitVideo from '../components/FunnelWaitVideo';
import FunnelWhyPixley, { WHY_PIXLEY_ART } from '../components/funnel/FunnelWhyPixley';
import { FunnelWelcomeFrame } from './FunnelWelcomePage';
import {
  FunnelScreenStyles,
  FunnelSky,
  FunnelNameStep,
  FunnelFriendStep,
  FunnelMeetStep,
  FunnelSigninStep,
  CheckIcon,
  sheetStepVariants,
  LoadingPandaVideo,
} from '../components/funnel/FunnelScreens';
import {
  FunnelExplainerStyles,
  ExplainerSlide,
  EXPLAINER_BY_KEY,
  EXPLAINER_STEPS,
  EXPLAINER_PRELOAD_AHEAD,
} from '../components/funnel/FunnelExplainer';
import {
  FunnelCheckoutStyles,
  FunnelPayStage,
  FunnelPaywallStep,
  FunnelCheckoutStep,
  FunnelSuccessStep,
  describeOffer,
  landingTermsFor,
  servedOfferVariant,
  validChosenCents,
  PRICING_UNAVAILABLE,
} from '../components/funnel/FunnelCheckout';
import { useFunnelAnalytics, FUNNEL_STARTED_KEY, FUNNEL_CHECKOUT_VIEW_ORDER, FUNNEL_CHECKOUT_FLOW_VERSION } from '../hooks/useFunnelAnalytics';
import { useOfferVariant, OFFER_FLAG_KEY } from '../hooks/useOfferVariant';
import { EVENTS } from '../constants/events';
import { loadTiktokPixel, captureTtclid, tiktokAttribution, tiktokTrack, trackTiktokAccountCreated } from '../utils/tiktokPixel';
import { AD_CLICK_PARAM, adClickReturnParam, captureAdClicks, funnelAttribution, googleReferred, metaTrack } from '../utils/adAttribution';
import { compressImage } from '../utils/imageCompression';
import { confirmCheckoutSession } from '../utils/funnelCheckout';
import { reportBeginCheckout, reportCheckoutReturn, reportSubscriptionConversion } from '../utils/googleAdsConversion';
import { PROMO_KEY, capturePromoCode, promoFromResponse } from '../utils/funnelPromo';
import { OFFER_TOKEN_KEY, OFFER_SOURCE, captureOfferToken, captureOfferPrice, clearOfferToken } from '../utils/funnelOffer';
import { blobToDataURL, characterUrlToPreview } from '../utils/previewImage';

// sessionStorage keys. The funnel's redirect_uri/state are NOT stored — they
// ride in the URL through the whole flow (incl. the Google round trip). Only
// the funnel's own answers persist here, so they can be saved to the account
// after sign-in.
const FUNNEL_STEP_KEY = 'rc_funnel_step';
const FUNNEL_NAME_KEY = 'rc_funnel_child_name';
const FUNNEL_PREVIEW_KEY = 'rc_funnel_preview_img';
// Written by AuthContext during a funnel sign-in only: whether the users row
// existed before that sign-in auto-created it (from the backend exchange)
const SIGNIN_USER_EXISTED_KEY = 'pixley_signin_user_existed';
// '1' once the visitor chose "See how Pixley works" on the welcome screen
// (retired 2026-10-23), which put the explainer slides ahead of picking a
// friend (and in the progress bar). Still read, so a session from before
// resumes on the same path.
const FUNNEL_EXPLAINER_KEY = 'rc_funnel_explainer';
// Photos above this are re-encoded to 1024 px JPEG before upload (see startGeneration)
const COMPRESS_PHOTO_ABOVE_BYTES = 400 * 1024;
// The intro entry's funnel_started flag (its name predates the merge of the
// two funnel pages)
const INTRO_STARTED_KEY = 'rc_funnel2_started';
// In-house checkout only: a random id for this funnel
// session, standing in for RevenueCat's `state` as the thing the pre-sign-in
// character-preview budget is scoped to; and the plan picked on the paywall,
// so a reload (or the hosted-Stripe fallback's round trip) lands back on it.
const FUNNEL_SESSION_KEY = 'pixley_funnel_session';
const FUNNEL_PLAN_KEY = 'pixley_funnel_plan';
// '1' while the character made in the funnel still has to be saved to the
// account: a returning account that isn't a member can only save its first
// character before paying (the backend's members-only gate), so the save
// waits for the purchase and runs on the success screen.
const FUNNEL_CHARACTER_PENDING_KEY = 'pixley_funnel_character_pending';
// A pay-link purchase that completed here: {name, image_url} of the character
// on the stage, so a reload of the welcome shows the welcome again (never the
// paywall) with nobody signed in
const PAY_LINK_PAID_KEY = 'pixley_funnel_paylink_paid';

const FUNNEL_KEYS = [
  FUNNEL_STEP_KEY, FUNNEL_NAME_KEY,
  FUNNEL_PREVIEW_KEY, SIGNIN_USER_EXISTED_KEY,
  FUNNEL_EXPLAINER_KEY,
  FUNNEL_SESSION_KEY, FUNNEL_PLAN_KEY, FUNNEL_CHARACTER_PENDING_KEY, PROMO_KEY, OFFER_TOKEN_KEY, PAY_LINK_PAID_KEY,
  // The retired paywall-eyebrow experiment's pin — cleared so a session that
  // started on an old bundle doesn't leave it behind
  'pixley_funnel_paywall_eyebrow',
  // The retired welcome experiment's pin — cleared so a session that started
  // on an old bundle doesn't leave it behind
  'rc_funnel_welcome_variant',
  // The retired age question and the retired free-book funnel's answers —
  // still cleared so a session that started on an old bundle doesn't leave
  // them behind in the tab.
  'rc_funnel_child_age',
  'rc_funnel_book_key', 'rc_funnel_book_prompt', 'rc_funnel_book_started',
  // Cleared too, so a second funnel run in the same tab counts as a new start
  FUNNEL_STARTED_KEY,
];
// The funnel's own answers also go to localStorage, so a parent who signs in,
// closes the tab and comes back days later resumes where they were — with
// their character — instead of a blank funnel that (still signed in, via the
// localStorage token) jumps straight to the paywall. sessionStorage stays the
// first copy (same-tab reloads); the localStorage copy expires after a week.
const RESUMABLE_KEYS = new Set([
  FUNNEL_STEP_KEY, FUNNEL_NAME_KEY, FUNNEL_PREVIEW_KEY, SIGNIN_USER_EXISTED_KEY,
  FUNNEL_EXPLAINER_KEY, FUNNEL_SESSION_KEY, FUNNEL_PLAN_KEY, FUNNEL_CHARACTER_PENDING_KEY,
]);
const RESUME_SAVED_AT_KEY = 'pixley_funnel_saved_at';
const RESUME_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const resumeCopyFresh = () => {
  try {
    const savedAt = Number(localStorage.getItem(RESUME_SAVED_AT_KEY));
    return savedAt > 0 && Date.now() - savedAt < RESUME_TTL_MS;
  } catch {
    return false;
  }
};
export const funnelGet = (key) => {
  const inTab = sessionStorage.getItem(key);
  if (inTab !== null || !RESUMABLE_KEYS.has(key) || !resumeCopyFresh()) return inTab;
  try {
    const kept = localStorage.getItem(key);
    if (kept !== null) sessionStorage.setItem(key, kept);
    return kept;
  } catch {
    return null;
  }
};
export const funnelSet = (key, value) => {
  sessionStorage.setItem(key, value);
  if (!RESUMABLE_KEYS.has(key)) return;
  try {
    localStorage.setItem(key, value);
    localStorage.setItem(RESUME_SAVED_AT_KEY, String(Date.now()));
  } catch {
    // Quota (the character image) or blocked storage: the tab copy still works
  }
};
const funnelRemove = (key) => {
  sessionStorage.removeItem(key);
  try { localStorage.removeItem(key); } catch { /* blocked storage */ }
};
export const clearFunnelSession = () => {
  FUNNEL_KEYS.forEach(funnelRemove);
  try { localStorage.removeItem(RESUME_SAVED_AT_KEY); } catch { /* blocked storage */ }
};

// iOS onboarding theme (NewOnboardingKit.swift)
const ACCENT = '#0A6CFF';
const CONFETTI_COLORS = [ACCENT, '#FFC83D', '#7C5CFF', '#34C759', '#FF6FA5'];

// Ready-made friends for visitors with no photo handy. `key` matches the
// backend's DEFAULT_AVATAR_KEYS (Pixley-Backend/routers/profiles_router.py) —
// the same starter characters new profiles get auto-assigned — so the funnel and
// the app offer one set, not two that drift apart.
//
// Served from /assets/funnel-friends/<key>.webp: 768px, 17-29KB each generated
// from the 1024px PNGs in /assets. 768 is exactly the cap blobToDataURL applies
// to a generated preview, so picking a friend and generating from a photo
// produce the same-sized character image downstream.
//
// `kind` is not rendered — it exists only for the aria-label, so a screen-reader
// user hears "Use Willow the deer" instead of a bare name for art they can't see.
const PRESET_FRIENDS = [
  { key: 'benny', name: 'Benny', kind: 'bunny' },
  { key: 'zippy-icon', name: 'Zippy', kind: 'tiger' },
  { key: 'willow', name: 'Willow', kind: 'deer' },
];

// ── flow ───────────────────────────────────────────────────────────────────
// The funnel can open with the explainer slides (FunnelExplainer): every visit
// on the intro entry (and a session from before the welcome screen was retired
// whose visitor chose "See how Pixley works" there). BASE_STEP_ORDER is the
// flow without them; the page prepends EXPLAINER_STEPS when they were chosen.
// Naming comes after the character reveal, right before sign-in: the visitor
// sees what they're getting before being asked for anything.
const BASE_STEP_ORDER = ['create', 'meet', 'name', 'signin'];
// Full flow order (incl. transient steps) — used to pick the slide direction
const FLOW_ORDER = [...EXPLAINER_STEPS, 'create', 'generating', 'meet', 'name', 'signin', 'finishing', 'paywall', 'checkout', 'success'];
// Steps that only the in-house checkout funnel has, after
// sign-in: pick a plan, pay inside the page, then the welcome.
const CHECKOUT_STEPS = ['paywall', 'checkout', 'success'];
// What a visit from a Google ad is told on its first screen, under the price
// line (FunnelCheckout.landingTermsFor)
const GOOGLE_LANDING_SAFETY = 'No ads · Parent controlled · Made for ages 2–8';
// How long the email pay-link's lookup may take before the link is treated
// as worthless and the funnel starts from the top
const PAY_LINK_LOOKUP_TIMEOUT_MS = 10000;
// Where a visitor who reloads mid-checkout is put back
const randomFunnelSession = () => {
  try {
    return crypto.randomUUID().replace(/-/g, '');
  } catch (_) {
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  }
};
const readSavedPlan = () => {
  try {
    const saved = JSON.parse(funnelGet(FUNNEL_PLAN_KEY) || 'null');
    return {
      tier: 'pro', // Pro is the funnel's only plan
      billing: saved?.billing === 'annual' ? 'annual' : 'monthly',
      // Pay what you want: the first month's price they named, kept across
      // the Google round trip and a reload (the pwyw arm only reads it)
      chosenCents: Number.isInteger(saved?.chosenCents) ? saved.chosenCents : null,
    };
  } catch (_) {
    return { tier: 'pro', billing: 'monthly', chosenCents: null };
  }
};

// Art to warm one step ahead of where it's shown
const PRELOAD_AHEAD = { ...EXPLAINER_PRELOAD_AHEAD, create: WHY_PIXLEY_ART };
// Where the header back button leads from each step
const BACK_TARGET = {
  episode: 'friend', personal: 'episode',
  meet: 'create', name: 'meet', signin: 'name',
  // checkout: no header back button — the order card's "Change" is the way back
};

// The funnel is a sign-up flow: accounts that existed before this sign-in
// get welcomed back and sent to the main site instead.
//
// The authoritative answer comes from the backend: the OAuth exchange checks
// for the users row BEFORE auto-creating it and returns user_already_existed,
// which AuthContext stashes under SIGNIN_USER_EXISTED_KEY (funnel sign-ins
// only). The heuristic below only covers sessions where no exchange happened
// in this tab (e.g. the user was already signed in when they opened the
// funnel link — which itself means the account predates the funnel visit).
const ACCOUNT_AGE_NEW_MS = 15 * 60 * 1000;
// Exported so other funnel code can make the same call from
// the same signals — "is this a returning account?" decides who gets sent home
// instead of on to checkout, and two copies of it would drift.
export const isExistingAccount = (user) => {
  if (!user) return false;
  const existedFlag = funnelGet(SIGNIN_USER_EXISTED_KEY);
  if (existedFlag === '1') return true;
  if (existedFlag === '0') return false;
  // Fallback inference: onboarded persona, any characters, or a row older
  // than this sign-in (fresh accounts are rows created seconds ago)
  let persona = user.persona;
  if (typeof persona === 'string') {
    try {
      persona = JSON.parse(persona);
    } catch {
      persona = null;
    }
  }
  if (persona && typeof persona === 'object' && !Array.isArray(persona) && Object.keys(persona).length > 0) return true;
  if (Array.isArray(user.characters) && user.characters.length > 0) return true;
  const joined = Date.parse(user.join_date || user.created_at || '');
  if (!Number.isNaN(joined) && Date.now() - joined > ACCOUNT_AGE_NEW_MS) return true;
  return false;
};


// Rotating status lines while the character generates
const GEN_MESSAGES = [
  'Pixley is sketching the outlines…',
  'Mixing the perfect colors…',
  'Adding a spark of magic…',
  'Putting on the finishing touches…',
];

// ── motion variants (step slide + staggered content reveal) ────────────────
const stepVariants = {
  enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 56 : -56, scale: 0.98 }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06,
      delayChildren: 0.04,
    },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir >= 0 ? -56 : 56,
    scale: 0.98,
    transition: { duration: 0.22, ease: 'easeIn' },
  }),
};

const itemVariants = {
  enter: { opacity: 0, y: 18 },
  center: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// ── image helpers ──────────────────────────────────────────────────────────

const dataURLtoBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

// ── meet-step confetti (from NOMeetPixleyStep) ─────────────────────────────

const Confetti = () => {
  const pieces = useRef(
    Array.from({ length: 45 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      dur: 2.2 + Math.random() * 1.6,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      drift: (Math.random() - 0.5) * 160,
      spin: 360 + Math.random() * 360,
    }))
  ).current;
  return (
    <div className="fa-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
};

/**
 * The web2app funnel, rebuilt as the iOS onboarding (new_onboarding/): pick a
 * friend or add a photo → watch Pixley bring them to life → name your hero →
 * sign in to save them → pick a plan and pay here → the welcome.
 *
 * `intro` is the cold-traffic entry (/funnel-intro, /funnel-auth/intro,
 * ?variant=intro): the SAME funnel, with the "how Pixley works" explainer
 * slides always up front (every visitor walks them before picking a friend)
 * and its own analytics name, so the two can be told apart in PostHog.
 */
const FunnelAuthPage = ({ intro = false }) => {
  // Whether this visitor is taking the explainer path (see FUNNEL_EXPLAINER_KEY).
  // On the intro entry everyone does.
  const [explained] = useState(() => intro || funnelGet(FUNNEL_EXPLAINER_KEY) === '1');
  const [searchParams] = useSearchParams();
  // The in-house checkout is the only path: the paywall and payment happen
  // here, at the end of the funnel. The hand-off to RevenueCat's hosted
  // funnel is retired; a link that still carries RevenueCat's redirect_uri /
  // state runs this same funnel and ignores them.
  const nativeCheckout = true;
  const STEP_ORDER = [
    ...(explained ? [...EXPLAINER_STEPS, ...BASE_STEP_ORDER] : BASE_STEP_ORDER),
    ...(nativeCheckout ? ['paywall'] : []),
  ];

  const { signInWithGoogle, isAuthenticated, loading, user } = useAuth();
  const { apiClient } = useAPI();
  const hasCompletedRef = useRef(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const [step, setStepState] = useState(() => {
    const saved = funnelGet(FUNNEL_STEP_KEY);
    // 'generating' can't be resumed after a reload — restart from create
    if (saved === 'generating') return 'create';
    // Naming comes after the character now. A session saved on the name step
    // with no character (it started when naming came first) makes one first.
    if (saved === 'name' && !funnelGet(FUNNEL_PREVIEW_KEY)) return 'create';
    // Checkout and success are not on the progress bar but are resumable:
    // a reload mid-payment lands on "back to plans", a reload on the welcome
    // stays on the welcome (never back through sign-in and a second save)
    if (saved && (STEP_ORDER.includes(saved) || CHECKOUT_STEPS.includes(saved))) return saved;
    // A fresh intro visit opens on the first explainer slide
    return intro ? EXPLAINER_STEPS[0] : 'create';
  });
  const [childName, setChildName] = useState(() => funnelGet(FUNNEL_NAME_KEY) || '');
  const [previewImage, setPreviewImage] = useState(() => funnelGet(FUNNEL_PREVIEW_KEY) || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  // Key of the ready-made friend currently being loaded (null = none)
  const [loadingFriend, setLoadingFriend] = useState(null);
  // What's selected on the create step: { type: 'friend', key } or
  // { type: 'photo' }. Nothing starts selected: the button waits for a choice.
  const [pick, setPick] = useState(null);
  // The ready-made friend on the meet and sign-in steps, introduced by name.
  // Null for a photo, and after a reload, when those steps use wording that
  // fits either.
  const [metFriend, setMetFriend] = useState(null);
  const [genProgress, setGenProgress] = useState(0);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [heroRevealed, setHeroRevealed] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [genMsgIndex, setGenMsgIndex] = useState(0);
  // The "welcome back" card before home: false, 'account' (a subscribed
  // account signing in) or 'member' (checkout refused: already a member)
  const [showExistingAccount, setShowExistingAccount] = useState(false);
  // ── in-house checkout state ─────────────────────────────────────────────
  // Offer terms from /subscription-plans; the plan under the finger; the
  // Embedded Checkout session (its client_secret lives here and nowhere
  // else — never in storage); and whether RevenueCat has caught up after
  // paying.
  const [plansData, setPlansData] = useState(null);
  // The plans request failed (network, 5xx): the paywall says so and offers
  // a retry. Nothing on the paywall is priced from anywhere but that response.
  const [plansError, setPlansError] = useState(false);
  const [plansAttempt, setPlansAttempt] = useState(0);
  // `?promo=CODE` from the landing URL, kept for the session; `promo` is the
  // backend's answer once it has said the code applies (utils/funnelPromo)
  const [promoCode] = useState(() => capturePromoCode());
  const [promo, setPromo] = useState(null);
  // ── the email's pay-link (utils/funnelOffer) ───────────────────────────
  // "Finish setting up Pixley" arrives with `?offer=`: a signed token naming
  // the recipient's account. Like a Shopify or Stripe checkout recovery link
  // it lets whoever holds it pay for THAT account, and nothing else: nobody
  // is signed in and no session is created, so it works on the computer
  // when the funnel was done on the phone, and forwarding it is harmless.
  // The page asks the backend what the link is worth:
  //   pending  → the opening is held back (a blank frame), nothing of the
  //              funnel shows before the answer
  //   ready    → straight to the paywall on the account's character, in the
  //              pay-what-you-want arm with the invitation; checkout and its
  //              read-back carry the token instead of a sign-in
  //   invalid  → a tampered or expired token, an account that already pays,
  //              or the backend not answering (Krish, 2026-10-18: fail
  //              closed): the token is dropped and, unless a character was
  //              already made in this session, the funnel starts from the
  //              top for whoever is here
  //   not mine → a signed-in visitor holding somebody else's link (the
  //              lookup compares the link's account with the session): they
  //              go to the home page (Krish, 2026-10-18)
  // The lookup waits for the stored session to be restored, so it can carry
  // the session and answer whose link it is.
  const [offerToken, setOfferToken] = useState(() => captureOfferToken());
  const [payLink, setPayLink] = useState(() => {
    //   done → paid through the link in this tab (the token is spent and
    //          gone); a reload stays on the welcome
    try {
      const paid = JSON.parse(sessionStorage.getItem(PAY_LINK_PAID_KEY) || 'null');
      if (paid && typeof paid === 'object') return { status: 'done', character: paid };
    } catch (_) {
      // no marker
    }
    return offerToken ? { status: 'pending', character: null } : null;
  });
  const payLinkReady = payLink?.status === 'ready';
  const payLinkPending = payLink?.status === 'pending';
  const payLinkInvalid = payLink?.status === 'invalid';
  const payLinkDone = payLink?.status === 'done';
  const walkFunnelRef = useRef(false);
  const markPayLinkInvalid = () => setPayLink((current) => (current?.status === 'pending' ? { status: 'invalid', character: null } : current));
  // 1. the lookup (once the stored session is restored, so it can say whose link it is)
  useEffect(() => {
    if (!offerToken || !payLinkPending || !apiClient || loading) return undefined;
    let cancelled = false;
    apiClient.resolveFunnelOffer(offerToken)
      .then(async (res) => {
        if (cancelled) return;
        if (res?.data?.valid === true && res.data.mine === false) {
          clearOfferToken();
          funnel.trackImmediate(EVENTS.FUNNEL_OFFER_NOT_THEIRS, {});
          window.location.replace('https://www.pixleyai.com');
          return;
        }
        const character = res?.data?.valid === true ? res.data.character : null;
        if (!character?.image_url) {
          markPayLinkInvalid();
          return;
        }
        // Shown only, never written where the post-sign-in save reads. The
        // stored image is fetched and run through the cut-out check BEFORE
        // the paywall shows, so a cut-out stands on the stage from the first
        // paint instead of flashing as a framed, cropped picture (Krish,
        // 2026-10-18); the URL itself is the fallback.
        const shown = await characterUrlToPreview(character.image_url);
        if (cancelled) return;
        setPayLink({ status: 'ready', character });
        setPreviewImage(shown);
        setChildName((current) => current || character.name || '');
        // The price chip tapped in the email, if any: the paywall opens with
        // it chosen (validChosenCents ignores anything the server does not
        // offer, and the chooser lets them change it)
        const tapped = captureOfferPrice();
        if (Number.isInteger(tapped)) {
          setPlan({ chosenCents: tapped });
          funnel.track(EVENTS.FUNNEL_PRICE_CHOSEN, { tier: 'pro', billing_period: 'monthly', cents: tapped, previous_cents: null, source: OFFER_SOURCE });
        }
        setShowWelcome(false);
        setStep('paywall');
      })
      .catch(() => { if (!cancelled) markPayLinkInvalid(); });
    return () => { cancelled = true; };
  }, [offerToken, payLinkPending, apiClient, loading]);  // eslint-disable-line react-hooks/exhaustive-deps
  // 2. never a blank page: a lookup that has not answered in time is a
  //    worthless link (fail closed), and a late answer is then ignored
  useEffect(() => {
    if (!payLinkPending) return undefined;
    const timer = setTimeout(markPayLinkInvalid, PAY_LINK_LOOKUP_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [payLinkPending]);  // eslint-disable-line react-hooks/exhaustive-deps
  // 2b. a reload after paying through the link: the welcome again, with the
  //     character on the stage and the membership shown active (the server
  //     confirmed the session before the marker was written)
  useEffect(() => {
    if (!payLinkDone) return;
    const paid = payLink.character || {};
    if (paid.image_url) setPreviewImage((current) => current || paid.image_url);
    if (paid.name) setChildName((current) => current || paid.name);
    setActivation('active');
    setShowWelcome(false);
    if (step !== 'success') setStep('success');
  }, [payLinkDone]);  // eslint-disable-line react-hooks/exhaustive-deps
  // 3. a worthless link: drop it and start a fresh funnel
  useEffect(() => {
    if (!payLinkInvalid) return;
    clearOfferToken();
    setOfferToken(null);
    funnel.track(EVENTS.FUNNEL_OFFER_INVALID, { had_character: !!funnelGet(FUNNEL_PREVIEW_KEY) });
    if (funnelGet(FUNNEL_PREVIEW_KEY)) return; // mid-funnel with a character: carry on
    // A fresh funnel: nothing of the link's account was ever fetched, and
    // nothing an earlier run left on this browser (a name, a plan, a promo
    // code, the started flag) is carried in either
    clearFunnelSession();
    setChildName('');
    walkFunnelRef.current = true;
    hasCompletedRef.current = false;
    setStep(intro ? EXPLAINER_STEPS[0] : 'create');
    setShowWelcome(true);
  }, [payLinkInvalid]);  // eslint-disable-line react-hooks/exhaustive-deps
  // When a code passed the lookup but Stripe refused it on the session
  const [promoNotice, setPromoNotice] = useState('');
  const [plan, setPlanState] = useState(readSavedPlan);
  // The paywall offer experiment (useOfferVariant): the arm PostHog dealt this
  // visitor, '' when they're not in the test, null for up to a second at load.
  // `offerVariant` is the arm the card is priced in (pwyw on the email's
  // pay-link, whatever was dealt) and null for none: the funnel's own offer,
  // control's price, but never reported as control. `offerServed` is the arm
  // the backend actually prices for it (control when the arm is unconfigured
  // server-side).
  const offerBucket = useOfferVariant();
  const offerVariant = payLinkReady ? 'pwyw' : offerBucket || null;
  const offerServed = offerVariant ? servedOfferVariant(plansData, offerVariant) : null;
  // In the experiment only when the card shows the arm PostHog dealt: not a
  // pay-link, not out of the test, not an arm the backend can't price
  const offerInTest = payLinkReady || offerBucket === '' ? false
    : offerBucket === null ? null
    : !plansData || offerServed === offerBucket;
  const [checkout, setCheckout] = useState(null); // { sessionId, clientSecret, publishableKey, trialDays, chargeValue, offerVariant, chosenCents }
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [activation, setActivation] = useState('pending');
  // 'morph' when the payment finished right here (checkout → success on the
  // shared stage); 'pop' when success was reached any other way
  const [successEntrance, setSuccessEntrance] = useState('pop');
  const funnelSessionRef = useRef(null);
  if (nativeCheckout && funnelSessionRef.current === null) {
    let id = funnelGet(FUNNEL_SESSION_KEY);
    if (!id || !/^[A-Za-z0-9_-]{16,128}$/.test(id)) {
      id = randomFunnelSession();
      funnelSet(FUNNEL_SESSION_KEY, id);
    }
    funnelSessionRef.current = id;
  }
  // Nothing chosen yet this session: the funnel is at its opening. False from
  // the first tap on, and after any reload mid-funnel.
  const [showWelcome, setShowWelcome] = useState(() => !funnelGet(FUNNEL_STEP_KEY));
  // Set once, at mount: a saved step means this is a reload or the return
  // from Google, not a fresh arrival — reported on funnel_started
  const resumedRef = useRef(!!funnelGet(FUNNEL_STEP_KEY));

  // ── the opening ────────────────────────────────────────────────────────
  // There is no welcome screen (the welcome experiment ended 2026-10-23 on
  // no-welcome): every visitor opens straight on picking a friend, or on the
  // explainer slides on the intro entry. 'pending' holds a blank frame while
  // the email's pay-link is looked up, so nothing of the funnel shows before
  // the answer; null otherwise.
  const opening = showWelcome && !CHECKOUT_STEPS.includes(step) && payLinkPending ? 'pending' : null;
  // A visit from a Google ad lands with the ad's terms in view (Google's
  // pricing policy: the price, the renewal and the cancellation on the page
  // the ad opens), said from the same offer the paywall will show
  const googleLanding = googleReferred();

  // Every funnel event carries the arm, so the drop-off can be split by it
  // without a join
  const funnel = useFunnelAnalytics(
    {
      // The paywall offer arm: the one the card is priced in (null when not
      // in the test) and, once the plans response is in, the arm actually
      // priced; plus the first month the parent named in the pwyw arm
      offer_variant: offerVariant,
      offer_served: plansData ? offerServed : null,
      offer_in_test: offerInTest,
      // posthog-js stamps every event with the flag's own answer, and the
      // experiment reads its arm from that stamp on the paywall view. A visit
      // not in the test clears it (an event's own property wins; undefined
      // leaves the stamp), so it is never counted in an arm it wasn't shown.
      [`$feature/${OFFER_FLAG_KEY}`]: offerInTest === false ? null : undefined,
      chosen_cents: validChosenCents(plansData, offerVariant, plan.chosenCents),
      // Why this visit is in the pwyw arm when the experiment did not deal it
      offer_source: payLinkReady ? OFFER_SOURCE : null,
      promo_code: promo?.code || null,
      // Which checkout this session ends in: ours, or RevenueCat's hosted one
      checkout: nativeCheckout ? 'pixley' : 'revenuecat',
    },
    // Both entries report the three checkout steps and flow version 5; the
    // intro entry keeps its own funnel name, path and started-flag so its
    // numbers stay separable in PostHog.
    {
      viewOrder: FUNNEL_CHECKOUT_VIEW_ORDER,
      flowVersion: FUNNEL_CHECKOUT_FLOW_VERSION,
      ...(intro ? { funnelName: 'web2app_intro', pathPrefix: '/funnel-intro', startedKey: INTRO_STARTED_KEY } : {}),
    }
  );

  // Slide direction: moving forward slides left, going back slides right
  const prevStepRef = useRef(step);
  const direction = FLOW_ORDER.indexOf(step) >= FLOW_ORDER.indexOf(prevStepRef.current) ? 1 : -1;
  useEffect(() => {
    prevStepRef.current = step;
  }, [step]);

  // TikTok Pixel lives on the funnel pages only (not site-wide): load it once
  // per page load and keep the ad click id (ttclid) from the landing URL so
  // it survives the Google OAuth round trip.
  useEffect(() => {
    loadTiktokPixel();
    captureTtclid();
    // Meta / Google click ids and UTMs, for the in-house checkout's
    // server-side conversions (harmless on the RevenueCat path)
    captureAdClicks();
  }, []);

  const setStep = useCallback((next) => {
    funnelSet(FUNNEL_STEP_KEY, next);
    setStepState(next);
    window.scrollTo({ top: 0 });
  }, []);

  // Warm the next explainer slide's art while this one is up — and the
  // generating step's pitch banner while they pick a friend. Not while the
  // opening screen is up: `step` is already 'create' behind it, and ~500 KB
  // of pitch art would race the welcome hero for a phone's bandwidth.
  const preloadedRef = useRef(new Set());
  useEffect(() => {
    if (opening) return;
    (PRELOAD_AHEAD[step] || []).forEach((src) => {
      if (preloadedRef.current.has(src)) return;
      preloadedRef.current.add(src);
      new Image().src = src;
    });
  }, [step, opening]);

  // Existing-account modal lingers a moment before leaving for the main site
  const redirectTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

  // Buffer the generating-step panda video while they pick a photo —
  // otherwise it only starts downloading when the wait screen mounts, which
  // on a slow connection means a blank circle at the funnel's most anxious
  // moment. The detached element is held in a ref so buffering isn't GC'd;
  // the wait screen's <video> then plays from the media cache instantly.
  const prefetchVideoRef = useRef(null);
  useEffect(() => {
    // `step` is already 'create' behind the opening screen; don't compete with
    // its art for bandwidth.
    if (opening || step !== 'create' || prefetchVideoRef.current) return;
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    [
      { src: '/assets/loading-panda.webm', type: 'video/webm' },
      { src: '/assets/loading-panda.mp4', type: 'video/mp4' },
    ].forEach(({ src, type }) => {
      const source = document.createElement('source');
      source.src = src;
      source.type = type;
      video.appendChild(source);
    });
    video.load();
    prefetchVideoRef.current = video;
  }, [step, opening]);

  // Warm the API connection (DNS + TCP + TLS) the moment a photo is chosen —
  // generation is one tap away, and a real request beats a preconnect hint
  // that would have idled out minutes before the user got here.
  const apiWarmedRef = useRef(false);
  useEffect(() => {
    if (!photoFile || apiWarmedRef.current || !apiClient) return;
    apiWarmedRef.current = true;
    apiClient.healthCheck().catch(() => {});
  }, [photoFile, apiClient]);

  // The name is never sent to analytics — only its length, so a truncated or
  // placeholder entry is still visible in the drop-off data.
  const submitName = (method) => {
    funnel.trackStepCompleted('name', {
      method,
      name_length: childName.trim().length,
    });
    setStep('signin');
  };

  // Funnel params. On first arrival RevenueCat passes `redirect_uri`/`state`;
  // after the Google round trip we land back here with the same values under
  // `funnel_redirect_uri`/`funnel_state` (prefixed so they don't collide with
  // the OAuth `state`/`code` Supabase appends). These stay in the URL the whole
  // time, so they're never cached — only the funnel's own answers (name,
  // character preview) live in sessionStorage.
  const redirectUri =
    searchParams.get('redirect_uri') || searchParams.get('funnel_redirect_uri');
  const funnelState =
    searchParams.get('state') || searchParams.get('funnel_state');

  // ── analytics: one step_viewed + virtual pageview per view ────────────────
  // Every step of this funnel lives at /funnel-auth, so nothing distinguishes
  // them in PostHog unless each one announces itself here.
  useEffect(() => {
    if (!nativeCheckout && (!redirectUri || !funnelState)) {
      funnel.trackInvalidLink();
      return;
    }
    // Nothing is on screen yet while the arm is undecided
    if (opening === 'pending') return;
    funnel.trackView(opening || step, { isResumed: resumedRef.current });
  }, [opening, step, redirectUri, funnelState, nativeCheckout, funnel]);

  // ── completion: save the held character, then bounce back to the funnel ──
  const completeAndRedirect = useCallback(async () => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    // In-house checkout: the funnel carries on to its own paywall. The ad
    // click that brought them here is stored against the account first (best
    // effort, never blocking) so the server-side conversions can match the
    // purchase later.
    const enterPaywall = async ({ isExisting }) => {
      try {
        await apiClient.recordFunnelAttribution(funnelAttribution());
      } catch (error) {
        logger.warn('Funnel attribution save failed (non-blocking):', error?.name || error);
      }
      funnel.trackStepCompleted('finishing', { is_existing_account: isExisting });
      hasCompletedRef.current = false; // the paywall is where this path ends
      setIsSigningIn(false);
      setStep('paywall');
    };

    // Hand the authenticated user back to the RevenueCat funnel (→ checkout /
    // next step) carrying the unchanged state + their app_user_id.
    const returnToFunnel = async ({ isExisting }) => {
      if (nativeCheckout) return enterPaywall({ isExisting });
      try {
        // TikTok click id + `_ttp` cookie ride along so the backend can send
        // the server-side Purchase (Events API) once RevenueCat reports the sale.
        const result = await apiClient.completeFunnelAuth(redirectUri, funnelState, tiktokAttribution());
        if (result.status && result.data?.redirect_url) {
          logger.log('✅ Funnel auth complete, returning to funnel');
          // Fire before clearFunnelSession() — it wipes the answers this event
          // reports — and send it unbatched, since the redirect below can
          // outrun PostHog's next flush.
          funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
            outcome: 'funnel_checkout',
            is_existing_account: isExisting,
            has_character: !!funnelGet(FUNNEL_PREVIEW_KEY),
          });
          clearFunnelSession();
          setShowSuccess(true);
          window.location.replace(result.data.redirect_url);
        } else {
          throw new Error(result.error || 'Failed to complete funnel authentication');
        }
      } catch (error) {
        logger.error('❌ Funnel auth completion error:', error);
        funnel.track(EVENTS.FUNNEL_COMPLETION_FAILED, {
          is_existing_account: isExisting,
          error_message: error.message || 'unknown',
          status_code: error.status || null,
        });
        hasCompletedRef.current = false;
        setIsSigningIn(false);
        setStep('signin');
        showToast(error.message || 'Something went wrong. Please try again.', 'error');
      }
    };

    // Keep the character they just made (mirrors the iOS flow, which uploads
    // the held preview after sign-in). Best effort, never blocks the funnel.
    // Used for a brand-new account below, and (Krish, 2026-10-18) for an
    // existing account WITHOUT a subscription, which is sent on to the
    // paywall for exactly this character; a subscribed account is welcomed
    // home untouched.
    const saveHeldCharacter = async () => {
      try {
        const previewDataUrl = funnelGet(FUNNEL_PREVIEW_KEY);
        const savedName = funnelGet(FUNNEL_NAME_KEY);
        if (previewDataUrl?.startsWith('data:') && savedName && user?.id) {
          await apiClient.saveFunnelCharacter({
            userId: user.id,
            name: savedName,
            animatedImage: dataURLtoBlob(previewDataUrl),
          });
          logger.log('✅ Funnel character saved to account');
        }
      } catch (error) {
        if (error?.code === 'SUBSCRIPTION_REQUIRED') {
          // Not a member yet and not their first character: saved once they've paid
          funnelSet(FUNNEL_CHARACTER_PENDING_KEY, '1');
          logger.log('Funnel character save waits for the purchase');
          return;
        }
        logger.warn('Funnel character save failed (non-blocking):', error);
      }
    };

    // An existing account keeps its persona untouched. Where it goes next
    // depends on whether it already has an active RevenueCat subscription:
    // only paying subscribers skip the funnel and get welcomed home —
    // everyone else keeps the character they made and is sent on to the
    // funnel checkout to (re)subscribe.
    if (isExistingAccount(user)) {
      let isSubscribed = false;
      try {
        // force=true busts the backend resolve cache for a fresh RevenueCat
        // read (they may have subscribed/lapsed since it was last cached).
        const sub = await apiClient.getSubscriptionStatus(user.id, { force: true });
        const tier = sub?.data?.subscription;
        isSubscribed = Boolean(sub?.data?.premium) || (!!tier && tier !== 'NONE');
      } catch (error) {
        // On an RC/backend hiccup, don't shortcut them home on an unverified
        // subscription — let them continue through the funnel checkout.
        logger.warn('Funnel: subscription check failed for returning account:', error);
      }

      funnel.track(EVENTS.FUNNEL_EXISTING_ACCOUNT, { is_subscribed: isSubscribed });

      if (isSubscribed) {
        logger.log('👋 Existing subscriber — skipping funnel, redirecting home');
        funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
          outcome: 'existing_subscriber_home',
          is_existing_account: true,
          has_character: !!funnelGet(FUNNEL_PREVIEW_KEY),
        });
        clearFunnelSession();
        setShowExistingAccount('account');
        redirectTimerRef.current = setTimeout(() => {
          window.location.replace('https://www.pixleyai.com');
        }, 3400);
        return;
      }

      logger.log('↩️ Existing account without an active subscription — continuing to funnel checkout');
      setStep('finishing');
      await saveHeldCharacter();
      await returnToFunnel({ isExisting: true });
      return;
    }

    setStep('finishing');

    // TikTok "created account" conversion (browser pixel). Only brand-new
    // accounts reach this point; returning accounts took the branch above.
    trackTiktokAccountCreated(user, { funnel: 'web2app' });

    await saveHeldCharacter();

    // Best-effort: seed the account persona with what the funnel learned
    // (the child's name) in the same shape OnboardingContext saves, so
    // stories personalize from day one — and since a non-empty persona marks
    // onboarding as done, funnel users aren't re-onboarded in-app. Existing
    // personas are merged, not replaced. The screen-time answer is analytics
    // only and is deliberately not written here.
    try {
      const personaName = funnelGet(FUNNEL_NAME_KEY);
      if (personaName && user?.id) {
        let existing = user.persona;
        if (typeof existing === 'string') {
          try {
            existing = JSON.parse(existing);
          } catch {
            existing = null;
          }
        }
        if (!existing || typeof existing !== 'object' || Array.isArray(existing)) existing = {};

        const persona = {
          favorite_shows: [],
          aggregated_topics: [],
          aggregated_interests: [],
          aggregated_preferences: [],
          profile_picture: null,
          discovery_source: [],
          ...existing,
          child_name: existing.child_name || personaName || null,
          // The funnel no longer asks for an age, but the key is still written
          // so an existing value survives the merge rather than being dropped
          // from the stored persona.
          child_age: existing.child_age || null,
          onboarding_source: existing.onboarding_source || 'web_funnel',
        };

        await apiClient.updateUserPersona(user.id, persona);
        logger.log('✅ Funnel persona saved to account');
      }
    } catch (error) {
      logger.warn('Funnel persona save failed (non-blocking):', error);
    }

    // Stamp the person so funnel sign-ups stay identifiable in cohorts long
    // after the funnel events themselves scroll out of the retention window.
    funnel.setPersonProperties({
      signup_source: 'web_funnel',
      funnel_name: 'web2app',
    });

    await returnToFunnel({ isExisting: false });
  }, [apiClient, redirectUri, funnelState, nativeCheckout, user, setStep, funnel]);

  // Fires when the user lands back authenticated after the OAuth round trip
  // (step === 'signin'), or arrives already signed in before starting
  // (step === 'create' untouched) — either way, finish and return to the funnel.
  useEffect(() => {
    // A pay-link visit pays for the token's account with nobody signed in:
    // there is nothing to complete, and nothing is decided while it is pending
    if (payLinkReady || payLinkPending || payLinkDone) return;
    if (loading || !isAuthenticated || !apiClient) return;
    if (hasCompletedRef.current) return;
    if (!nativeCheckout && (!redirectUri || !funnelState)) {
      logger.warn('Funnel auth: missing redirect_uri or state');
      return;
    }
    // Already past sign-in on the in-house path: nothing to complete
    if (CHECKOUT_STEPS.includes(step)) return;
    // Funnel data is waiting to be saved (character and/or persona) — hold
    // until the user object (and its id) has loaded so the saves aren't
    // silently skipped
    const hasFunnelData =
      funnelGet(FUNNEL_PREVIEW_KEY) ||
      funnelGet(FUNNEL_NAME_KEY);
    if (hasFunnelData && !user?.id) return;
    // A worthless pay-link, nothing made yet: they walk the funnel from the
    // top (the effect above started it); the jump to the paywall waits until
    // they have a character
    if (walkFunnelRef.current && !hasFunnelData) return;
    walkFunnelRef.current = false;
    if (step === 'signin' || step === 'create') {
      completeAndRedirect();
    }
  }, [isAuthenticated, loading, apiClient, redirectUri, funnelState, nativeCheckout, step, user, completeAndRedirect, payLinkReady, payLinkPending, payLinkDone]);

  // ── in-house checkout ───────────────────────────────────────────────────
  // Nobody reaches the paywall signed out: a reload that lost the session goes
  // back to sign-in (the character and name are still in sessionStorage).
  useEffect(() => {
    if (!nativeCheckout || loading || isAuthenticated || payLinkReady || payLinkPending || payLinkDone) return;
    // A worthless pay-link has just restarted the funnel (effect 3 above, in
    // this same commit): its step wins, not a bounce to sign-in
    if (walkFunnelRef.current) return;
    if (CHECKOUT_STEPS.includes(step)) setStep('signin');
  }, [nativeCheckout, loading, isAuthenticated, step, setStep, payLinkReady, payLinkPending, payLinkDone]);

  // Signed in and past sign-in, but this browser no longer has the character
  // (another device, cleared storage, or a visit from before the answers were
  // kept past the tab): show the one saved to their account at sign-in, so
  // the paywall stars their character instead of the stand-in panda. Shown
  // only — the URL never goes into FUNNEL_PREVIEW_KEY, which holds the data
  // URL the post-sign-in save uploads.
  const characterRestoreRef = useRef(false);
  useEffect(() => {
    if (!nativeCheckout || loading || !isAuthenticated || !user?.id || !apiClient || payLinkReady || payLinkDone) return;
    if (previewImage || !CHECKOUT_STEPS.includes(step) || characterRestoreRef.current) return;
    characterRestoreRef.current = true;
    apiClient.getUserCharacters(user.id)
      .then((res) => {
        const chars = ((res?.status && res.data) || []).filter((c) => c?.animated_image_url);
        if (!chars.length) return;
        const newest = chars.reduce((a, b) =>
          (Date.parse(b.created_at || '') || 0) > (Date.parse(a.created_at || '') || 0) ? b : a);
        // Processed before it is shown, so it never flashes as a framed picture
        setChildName((current) => current || newest.name || '');
        return characterUrlToPreview(newest.animated_image_url).then((shown) => setPreviewImage(shown));
      })
      .catch((error) => logger.warn('Funnel character restore failed (non-blocking):', error?.name || error));
  }, [nativeCheckout, loading, isAuthenticated, user, apiClient, previewImage, step, payLinkReady, payLinkDone]);

  // Past sign-in the welcome is behind them: a return visit that lands on the
  // paywall must not open on it (its Continue would restart at 'create')
  useEffect(() => {
    if (CHECKOUT_STEPS.includes(step)) setShowWelcome(false);
  }, [step]);

  // Offer terms, fetched while they sign in so the paywall has them on arrival
  // (on arrival for a visit from a Google ad, whose first screen states them)
  const plansRequestedRef = useRef(false);
  useEffect(() => {
    if (!nativeCheckout || !apiClient || plansRequestedRef.current) return;
    if (!googleLanding && !['signin', 'finishing', ...CHECKOUT_STEPS].includes(step)) return;
    plansRequestedRef.current = true;
    apiClient.getSubscriptionPlans()
      .then((res) => {
        if (res?.status && res.data) {
          setPlansData(res.data);
          setPlansError(false);
        } else {
          setPlansError(true);
        }
      })
      .catch((error) => {
        logger.warn('Funnel plans fetch failed:', error?.name || error);
        setPlansError(true);
      });
    if (promoCode) {
      apiClient.getFunnelPromo(promoCode)
        .then((res) => setPromo(promoFromResponse(res)))
        .catch((error) => logger.warn('Funnel promo lookup failed:', error?.name || error));
    }
  }, [nativeCheckout, apiClient, step, promoCode, plansAttempt, googleLanding]);
  const retryPlans = () => {
    plansRequestedRef.current = false;
    setPlansError(false);
    setPlansData(null);
    setPlansAttempt((n) => n + 1);
  };

  // Hosted-Stripe fallback round trip: Stripe sends them back here with
  // `checkout=success&session_id=cs_…` / `checkout=cancelled`. The URL alone
  // proves nothing (anyone can type it), so the welcome only goes up once our
  // server has read that session back from Stripe (owner-only) and seen it
  // paid; until then the paywall shows with its button busy.
  const hostedReturnRef = useRef(false);
  useEffect(() => {
    if (!nativeCheckout || hostedReturnRef.current || loading || !isAuthenticated) return;
    const outcome = searchParams.get('checkout');
    if (!outcome) return;
    hostedReturnRef.current = true;
    const sessionId = searchParams.get('session_id') || '';
    // Handled once: drop the outcome from the URL so a reload does not
    // report a second purchase
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      url.searchParams.delete('session_id');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (_) {
      // leave the URL as it is
    }
    if (outcome !== 'success') {
      setStep('paywall');
      return undefined;
    }
    let stale = false;
    setStep('paywall');
    setCheckoutBusy(true);
    setCheckoutError('');
    confirmCheckoutSession(apiClient, sessionId).then((verified) => {
      if (stale) return;
      setCheckoutBusy(false);
      if (!verified) {
        funnel.trackImmediate(EVENTS.FUNNEL_PURCHASE_VERIFIED, {
          verified: false, tier: plan.tier, billing_period: plan.billing, method: 'stripe_hosted',
        });
        setCheckoutError("We couldn't confirm that payment. If you were charged, your membership will appear in a moment.");
        return;
      }
      funnel.trackImmediate(EVENTS.FUNNEL_PURCHASE_COMPLETED, {
        tier: plan.tier, billing_period: plan.billing, method: 'stripe_hosted',
      });
      funnel.trackImmediate(EVENTS.FUNNEL_PURCHASE_VERIFIED, {
        verified: true, tier: plan.tier, billing_period: plan.billing, method: 'stripe_hosted',
      });
      reportCheckoutReturn(apiClient, { sessionId, tier: plan.tier, billing: plan.billing, email: user?.email });
      setActivation('pending');
      setSuccessEntrance('pop');
      setStep('success');
    });
    return () => { stale = true; };
  }, [nativeCheckout, loading, isAuthenticated, searchParams, plan, funnel, setStep, apiClient, user?.email]);

  const setPlan = (next) => {
    const merged = { ...plan, ...next };
    funnelSet(FUNNEL_PLAN_KEY, JSON.stringify(merged));
    setPlanState(merged);
  };
  const selectBilling = (billing) => {
    if (billing === plan.billing) return;
    funnel.track(EVENTS.FUNNEL_BILLING_PERIOD_CHANGED, { tier: plan.tier, billing_period: billing });
    setPlan({ billing });
  };

  const offer = useMemo(
    () => describeOffer(plansData, plan.tier, plan.billing, promo, { variant: offerVariant, chosenCents: plan.chosenCents }),
    [plansData, plan.tier, plan.billing, promo, offerVariant, plan.chosenCents]
  );
  // A Google-ad visit's first-screen price line: Pro monthly, as the ads say
  const landingTerms = useMemo(
    () => (googleLanding ? landingTermsFor(describeOffer(plansData, 'pro', 'monthly', promo, { variant: offerVariant })) : ''),
    [googleLanding, plansData, promo, offerVariant]
  );
  // Pay what you want: the chooser's handlers (the pwyw arm, monthly)
  const choosePrice = (cents) => {
    funnel.track(EVENTS.FUNNEL_PRICE_CHOSEN, {
      tier: plan.tier, billing_period: plan.billing, cents, previous_cents: plan.chosenCents,
    });
    setPlan({ chosenCents: cents });
  };
  const priceChooserOpened = () => {
    funnel.track(EVENTS.FUNNEL_PRICE_CHOOSER_OPENED, { tier: plan.tier, billing_period: plan.billing, chosen_cents: plan.chosenCents });
  };
  // What the session is asked to price: the served arm, and the named price
  // only where it applies (the pwyw arm, monthly)
  const checkoutExperiment = (billing) => ({
    offerVariant: offerServed,
    chosenCents: billing === 'monthly' ? validChosenCents(plansData, offerVariant, plan.chosenCents) : null,
  });

  // The server refused checkout with 409 (the result's httpStatus): RevenueCat says this account already
  // has a membership (a second tab, a reload on the paywall, a subscription
  // made in the app since). Nothing to pay: the "welcome back" card, then home.
  const sendMemberHome = useCallback((method) => {
    funnel.trackImmediate(EVENTS.FUNNEL_CHECKOUT_FAILED, { method, error_message: 'already_subscribed' });
    clearTimeout(redirectTimerRef.current);
    clearFunnelSession();
    setCheckout(null);
    setShowExistingAccount('member');
    redirectTimerRef.current = setTimeout(() => {
      window.location.replace('https://www.pixleyai.com');
    }, 3400);
  }, [funnel]);

  // Stripe's hosted page, when the embedded form can't be shown here
  const hostedCheckoutRef = useRef(false);
  const startHostedCheckout = useCallback(async () => {
    if (hostedCheckoutRef.current) return;
    if (payLinkReady) {
      // The hosted fallback returns to this page expecting a signed-in owner,
      // which a pay-link visit never has: rather than a dead "taking you to
      // Stripe…" the paywall comes back with a retry
      funnel.track(EVENTS.FUNNEL_CHECKOUT_FAILED, { method: 'stripe_embedded', error_message: 'embedded_unavailable_pay_link' });
      setCheckoutError("The card form couldn't load here. Please try again.");
      setCheckout(null);
      setStep('paywall');
      return;
    }
    if (!user?.id) return;
    hostedCheckoutRef.current = true;
    try {
      const res = await apiClient.createFunnelCheckoutSession({
        userId: user.id,
        userEmail: user.email,
        tier: plan.tier,
        billingPeriod: plan.billing,
        embedded: false,
        attribution: funnelAttribution(),
        promoCode: promo?.code || null,
        ...checkoutExperiment(plan.billing),
        // Back to this same entry (the intro entry keeps its analytics name)
        returnUrl: `${window.location.origin}/funnel-auth${intro ? '?variant=intro' : ''}`,
      });
      if (res?.httpStatus === 409) {
        hostedCheckoutRef.current = false;
        sendMemberHome('stripe_hosted');
        return;
      }
      const url = res?.data?.checkout_url;
      // Only Stripe's own checkout origin is ever navigated to
      if (res?.status && typeof url === 'string' && /^https:\/\/checkout\.stripe\.com\//.test(url)) {
        funnel.trackImmediate(EVENTS.FUNNEL_CHECKOUT_STARTED, {
          tier: plan.tier, billing_period: plan.billing, method: 'stripe_hosted',
          // No arm asked (not in the test): the server prices the funnel's
          // own offer and calls it control, which this visit is not
          offer_variant: offerServed ? res?.data?.offer_variant || offerServed : null,
          chosen_cents: Number.isInteger(res?.data?.chosen_cents) ? res.data.chosen_cents : null,
        });
        reportBeginCheckout({
          tier: plan.tier, billing: plan.billing, email: user?.email,
          value: Number.isInteger(res?.data?.chosen_cents) ? res.data.chosen_cents / 100 : null,
        });
        window.location.assign(url);
        return;
      }
      throw new Error(res?.error || 'Could not open checkout');
    } catch (error) {
      hostedCheckoutRef.current = false;
      funnel.track(EVENTS.FUNNEL_CHECKOUT_FAILED, { method: 'stripe_hosted', error_message: error?.message || 'unknown' });
      setCheckoutError("We couldn't open checkout. Please try again.");
      setStep('paywall');
    }
  }, [apiClient, user, plan, funnel, setStep, intro, promo, offerServed, payLinkReady, sendMemberHome]);  // eslint-disable-line react-hooks/exhaustive-deps

  // A plan card's button: an Embedded Checkout session for that plan
  const startCheckout = async (tier) => {
    if (checkoutBusy || (!user?.id && !payLinkReady)) return;
    if (!offer.available) {
      // Never start a session for an amount the page could not show
      setCheckoutError(PRICING_UNAVAILABLE);
      return;
    }
    const chosen = { tier: 'pro', billing: plan.billing }; // the funnel sells Pro only
    setPlan(chosen);
    setCheckoutBusy(true);
    setCheckoutError('');
    const asked = checkoutExperiment(chosen.billing);
    funnel.track(EVENTS.FUNNEL_PLAN_SELECTED, {
      tier: chosen.tier, billing_period: chosen.billing, offer_variant: asked.offerVariant, chosen_cents: asked.chosenCents,
    });
    funnel.trackStepCompleted('paywall', {
      tier: chosen.tier, billing_period: chosen.billing, offer_kind: offer.kind,
      offer_variant: asked.offerVariant, chosen_cents: asked.chosenCents,
    });
    // The promo as it stood at the tap: the session is created with exactly
    // this, and the order card prices from what the session got
    const promoUsed = promo;
    try {
      const res = await apiClient.createFunnelCheckoutSession({
        // A pay-link session is for the token's account, not whoever is here
        userId: payLinkReady ? null : user?.id,
        userEmail: payLinkReady ? null : user?.email,
        tier: chosen.tier,
        billingPeriod: chosen.billing,
        embedded: true,
        offerToken: payLinkReady ? offerToken : null,
        attribution: funnelAttribution(),
        promoCode: promoUsed?.code || null,
        ...asked,
      });
      // The API client resolves a refusal ({ success: false, httpStatus }), it never throws
      if (res?.httpStatus === 409) {
        sendMemberHome('stripe_embedded');
        return;
      }
      const clientSecret = res?.data?.client_secret;
      const publishableKey = plansData?.stripe_publishable_key;
      if (!res?.status || !clientSecret) throw new Error(res?.error || 'Could not start checkout');
      const promoApplied = !!promoUsed && res.data.promo_applied === true;
      if (promoUsed && !promoApplied) {
        // Stripe would not take the code on this session (first-time-customer
        // rule, minimum amount, expired since): the regular offer applies and
        // the card must say so
        setPromo(null);
        setPromoNotice(`Code ${promoUsed.code} couldn't be applied, so the regular offer applies.`);
      } else {
        setPromoNotice('');
      }
      // The arm and the price as the server applied them (never as asked):
      // the order card and the events read from what Stripe will charge
      const applied = {
        // No arm asked (not in the test): none applied, though the server
        // names its default offer control
        variant: asked.offerVariant ? res.data.offer_variant || asked.offerVariant : null,
        chosenCents: Number.isInteger(res.data.chosen_cents) ? res.data.chosen_cents : null,
      };
      const offerAtCheckout = describeOffer(plansData, chosen.tier, chosen.billing, promoApplied ? promoUsed : null, applied);
      funnel.track(EVENTS.FUNNEL_CHECKOUT_STARTED, {
        tier: chosen.tier,
        billing_period: chosen.billing,
        method: publishableKey ? 'stripe_embedded' : 'stripe_hosted',
        trial_days: res.data.trial_days ?? 0,
        offer_variant: applied.variant,
        chosen_cents: applied.chosenCents,
      });
      reportBeginCheckout({
        tier: chosen.tier, billing: chosen.billing, email: user?.email,
        value: offerAtCheckout.chargeValue,
      });
      // Browser-side "started checkout" for the ad networks (no amounts: the
      // purchase itself is reported server-side with dedup)
      metaTrack('InitiateCheckout', { content_name: `pixley_${chosen.tier}_${chosen.billing}`, currency: 'USD' });
      tiktokTrack('InitiateCheckout', { content_name: `pixley_${chosen.tier}_${chosen.billing}`, content_type: 'product' });
      if (!publishableKey) {
        // No key for Embedded Checkout on this build: Stripe's page instead
        await startHostedCheckout();
        return;
      }
      setCheckout({
        sessionId: res.data.session_id,
        clientSecret,
        publishableKey,
        trialDays: Number(res.data.trial_days || 0),
        offer: offerAtCheckout,
        chargeValue: offerAtCheckout.chargeValue,
        offerVariant: applied.variant,
        chosenCents: applied.chosenCents,
      });
      setStep('checkout');
    } catch (error) {
      funnel.track(EVENTS.FUNNEL_CHECKOUT_FAILED, { method: 'stripe_embedded', error_message: error?.message || 'unknown' });
      setCheckoutError(error?.message || "We couldn't start checkout. Please try again.");
    } finally {
      setCheckoutBusy(false);
    }
  };

  // Stripe reported the payment complete (no reload: redirect_on_completion
  // is "never"). The welcome goes up on Stripe's word; our server then reads
  // the session back from Stripe (funnel_purchase_verified) while the page
  // waits for RevenueCat's entitlement. The purchase itself is reported to
  // the ad networks server-side (Meta by RevenueCat's Meta Ads integration
  // from the Customer attributes set at sign-in, TikTok by our RevenueCat
  // webhook), so no pixel purchase event is fired here: it could not be
  // deduplicated against RevenueCat's and would count twice.
  const activationRef = useRef(null);
  const onCheckoutComplete = useCallback(() => {
    const trial = (checkout?.trialDays || 0) > 0;
    const sessionId = checkout?.sessionId;
    // What the first period cost, in cents, in every arm (the pwyw arm's
    // chosen_cents is the same number): the experiment's revenue metric
    const firstPeriodCents = Number.isFinite(Number(checkout?.chargeValue)) ? Math.round(Number(checkout.chargeValue) * 100) : null;
    funnel.trackImmediate(EVENTS.FUNNEL_PURCHASE_COMPLETED, {
      tier: plan.tier, billing_period: plan.billing, method: 'stripe_embedded', is_trial: trial,
      offer_variant: checkout?.offerVariant || null, chosen_cents: checkout?.chosenCents ?? null,
      first_period_cents: firstPeriodCents,
    });
    funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
      outcome: 'purchased',
      has_character: !!funnelGet(FUNNEL_PREVIEW_KEY),
    });
    const offer = checkout?.offer;
    const paidThroughLink = payLinkReady ? offerToken : null;
    if (paidThroughLink) {
      // The link has done its job: forget the token so a reload (or the
      // back button) can never reopen a paywall for an account that just
      // paid, and remember the welcome instead
      clearOfferToken();
      setOfferToken(null);
      try {
        sessionStorage.setItem(PAY_LINK_PAID_KEY, JSON.stringify(payLink?.character || {}));
      } catch (_) {
        // a reload will land on sign-in instead, which is safe
      }
      setPayLink({ status: 'done', character: payLink?.character || null });
    }
    confirmCheckoutSession(apiClient, sessionId, { offerToken: paidThroughLink }).then((verified) => {
      // A pay-link has no session to poll RevenueCat with: the server's
      // read-back of the paid session is the activation signal
      if (paidThroughLink) setActivation(verified ? 'active' : 'slow');
      funnel.trackImmediate(EVENTS.FUNNEL_PURCHASE_VERIFIED, {
        verified, tier: plan.tier, billing_period: plan.billing, is_trial: trial,
        offer_variant: checkout?.offerVariant || null, chosen_cents: checkout?.chosenCents ?? null,
        first_period_cents: firstPeriodCents,
      });
      // Google Ads: every funnel purchase, here at the payment — the hand-off
      // to the website carries no session id, so it isn't counted again there.
      // Value: what was charged; a free trial counts at its plan's price.
      if (verified) {
        reportSubscriptionConversion({
          sessionId,
          value: Number(checkout?.chargeValue) > 0 ? Number(checkout.chargeValue) : Number(offer?.regular) || 0,
          email: user?.email,
        });
      }
    });
    setCheckout(null); // the secret has done its job
    setActivation('pending');
    setSuccessEntrance('morph');
    setStep('success');
  }, [checkout, plan, funnel, setStep, apiClient, user?.email, payLinkReady, offerToken, payLink?.character]);

  // The held character of a returning, non-member account (see
  // FUNNEL_CHARACTER_PENDING_KEY): saved once the membership is active, before
  // the success screen hands off to the app and clears the funnel's answers.
  const pendingCharacterSaveRef = useRef(null);
  useEffect(() => {
    if (step !== 'success' || activation !== 'active' || !user?.id || !apiClient) return;
    if (pendingCharacterSaveRef.current || funnelGet(FUNNEL_CHARACTER_PENDING_KEY) !== '1') return;
    const previewDataUrl = funnelGet(FUNNEL_PREVIEW_KEY);
    const savedName = funnelGet(FUNNEL_NAME_KEY);
    if (!previewDataUrl?.startsWith('data:') || !savedName) {
      funnelRemove(FUNNEL_CHARACTER_PENDING_KEY);
      return;
    }
    pendingCharacterSaveRef.current = apiClient
      .saveFunnelCharacter({ userId: user.id, name: savedName, animatedImage: dataURLtoBlob(previewDataUrl) })
      .then(() => {
        funnelRemove(FUNNEL_CHARACTER_PENDING_KEY);
        logger.log('✅ Funnel character saved to account after the purchase');
      })
      .catch((error) => logger.warn('Funnel character save after the purchase failed (non-blocking):', error));
  }, [step, activation, user?.id, apiClient]);

  // Poll past the caches until RevenueCat shows the new entitlement
  useEffect(() => {
    if (!nativeCheckout || step !== 'success' || !user?.id || !apiClient) return undefined;
    let stopped = false;
    const startedAt = Date.now();
    const tick = async () => {
      if (stopped) return;
      try {
        const sub = await apiClient.getSubscriptionStatus(user.id, { force: true });
        const tier = sub?.data?.subscription;
        if (Boolean(sub?.data?.premium) || (tier && tier !== 'NONE')) {
          if (!stopped) setActivation('active');
          return;
        }
      } catch (_) {
        // keep polling
      }
      if (Date.now() - startedAt > 60000) {
        if (!stopped) setActivation('slow');
        return;
      }
      activationRef.current = setTimeout(tick, 2500);
    };
    tick();
    return () => {
      stopped = true;
      clearTimeout(activationRef.current);
    };
  }, [nativeCheckout, step, user, apiClient]);

  // Off to the app. `upgraded=true` hands the home screen the same
  // activation-and-welcome path a web paywall purchase takes.
  const finishToApp = async () => {
    // A character still being saved finishes first (clearFunnelSession drops it)
    if (pendingCharacterSaveRef.current) {
      await Promise.race([pendingCharacterSaveRef.current, new Promise((r) => setTimeout(r, 8000))]);
    }
    funnel.trackStepCompleted('success', { activation });
    const home = new URL('https://www.pixleyai.com/');
    home.searchParams.set('upgraded', 'true');
    home.searchParams.set('tier', plan.tier);
    home.searchParams.set('billing', plan.billing);
    clearFunnelSession();
    window.location.replace(home.toString());
  };

  // ── generation (same /previewCharacterImage the iOS onboarding calls) ────
  useEffect(() => {
    if (step !== 'generating') return undefined;
    // Ease toward 95%, slowing as it approaches — keeps the bar alive during
    // an open-ended wait without ever finishing early (NewOnboardingFlow)
    const id = setInterval(() => {
      setGenProgress((p) => (p >= 0.95 ? p : p + (0.95 - p) * 0.08));
    }, 500);
    return () => clearInterval(id);
  }, [step]);

  // Cycle the "Pixley is painting" status lines while generating
  useEffect(() => {
    if (step !== 'generating') return undefined;
    setGenMsgIndex(0);
    const id = setInterval(() => setGenMsgIndex((i) => (i + 1) % GEN_MESSAGES.length), 2600);
    return () => clearInterval(id);
  }, [step]);

  const startGeneration = async (original) => {
    setGenProgress(0);
    setStep('generating');

    const startedAt = Date.now();
    // Counts the /previewCharacterImage attempts this run actually spent, so a
    // slow-but-successful retry is distinguishable from a first-try success.
    let attempts = 0;

    // Shrink the photo before it leaves the phone. A camera photo is 2-5 MB
    // and took 20-100 s to upload over mobile in the Google-ad replays of
    // 10/20 — most of what looked like a hung progress bar. 1024 px JPEG is
    // what the backend resizes to anyway. Anything the browser can't decode
    // (HEIC on Android, say) goes up as-is, as before.
    let image = original;
    if (original && original.size > COMPRESS_PHOTO_ABOVE_BYTES) {
      try {
        image = await compressImage(original, { outputFileName: 'character.jpg' });
      } catch (_) {
        image = original;
      }
    }
    funnel.track(EVENTS.FUNNEL_GENERATION_STARTED, {
      photo_size_kb: original?.size ? Math.round(original.size / 1024) : null,
      upload_size_kb: image?.size ? Math.round(image.size / 1024) : null,
      compressed: image !== original,
    });

    try {
      let tokenResp;
      try {
        tokenResp = await apiClient.getFunnelPreviewToken(redirectUri, funnelState, {
          funnelSession: funnelSessionRef.current,
        });
      } catch (error) {
        if (error.status === 429) {
          // Minting is IP rate-limited as a flood backstop; the generation
          // budget itself is charged later, on /previewCharacterImage.
          throw new Error("You've hit the character-preview limit for now. Please wait a bit and try again.");
        }
        throw error;
      }
      const previewToken = tokenResp.token || tokenResp.data?.token;
      if (!previewToken) throw new Error('Could not start character generation. Please try again.');

      // 2 auto-attempts max. Each one charges the per-IP budget
      // (WEB_PREVIEW_IP_MAX_PER_HOUR, 8/hour) as well as the per-session one,
      // so keep this small — it must leave room for manual "try a different
      // photo" do-overs before the visitor hits a 429.
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        attempts += 1;
        try {
          const blob = await apiClient.previewCharacterImageWeb({
            image,
            handdrawn: false,
            previewToken,
          });
          const dataUrl = await blobToDataURL(blob, 768, { alpha: true });
          funnelSet(FUNNEL_PREVIEW_KEY, dataUrl);
          setPreviewImage(dataUrl);
          setGenProgress(1);
          setHeroRevealed(false);
          funnel.track(EVENTS.FUNNEL_GENERATION_COMPLETED, {
            duration_seconds: Math.round((Date.now() - startedAt) / 1000),
            attempts,
          });
          funnel.trackStepCompleted('create', { creation_method: 'photo' });
          setTimeout(() => setStep('meet'), 400);
          return;
        } catch (error) {
          lastError = error;
          // Only retry faults that might be transient. A 4xx is deterministic for
          // this input — safety block, blocked name, oversize photo, or a spent
          // preview budget (429) — so retrying returns the same answer AND spends
          // another slot from the per-IP budget. 5xx can be a blip, so retry those.
          if (error.status >= 400 && error.status < 500) break;
          if (/inappropriate|safety|copyright/i.test(error.message || '')) break;
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
      throw lastError || new Error('Character generation failed');
    } catch (error) {
      logger.error('❌ Funnel preview generation failed:', error);
      funnel.track(EVENTS.FUNNEL_GENERATION_FAILED, {
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
        attempts,
        error_message: error.message || 'unknown',
        status_code: error.status || null,
        // Safety blocks and rate limits are the two failures worth splitting
        // out: one is content, the other is capacity.
        is_rate_limited: error.status === 429,
        is_safety_block: /inappropriate|safety|copyright/i.test(error.message || ''),
      });
      showToast(error.message || "We couldn't bring your character to life. Please try again.", 'error');
      setStep('create');
    }
  };

  const handleGenerate = () => {
    if (!photoFile) {
      showToast('Add a photo first!', 'warning');
      return;
    }
    startGeneration(photoFile);
  };

  // Picking a ready-made friend skips image generation entirely: no
  // /previewCharacterImage call, so it costs nothing, never charges the per-IP
  // preview budget, and can't fail on a safety block. The chosen art is written
  // into FUNNEL_PREVIEW_KEY in exactly the shape a generated preview takes
  // (768px JPEG data URL), so the meet step, sign-in and saveFunnelCharacter
  // downstream need no special-casing — the character is still saved under the
  // child's name, the friend is just the artwork standing in for a photo.
  const choosePresetFriend = async (friend) => {
    if (loadingFriend) return;
    setLoadingFriend(friend.key);
    try {
      const response = await fetch(`/assets/funnel-friends/${friend.key}.webp`);
      if (!response.ok) throw new Error(`Could not load ${friend.name}`);
      const dataUrl = await blobToDataURL(await response.blob(), 768);
      funnelSet(FUNNEL_PREVIEW_KEY, dataUrl);
      setPreviewImage(dataUrl);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        friend_name: friend.name,
        success: true,
        // Whether the escape hatch is used instead of a photo, or after one
        // was already added, says different things about the photo ask.
        had_photo: !!photoFile,
      });
      funnel.trackStepCompleted('create', { creation_method: 'preset_friend' });
      // No progress bar to run down — straight to the reveal
      setHeroRevealed(false);
      setStep('meet');
    } catch (error) {
      logger.error('Preset friend selection failed:', error);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        success: false,
        error_message: error.message || 'unknown',
      });
      showToast("We couldn't load that friend. Please try another.", 'error');
    } finally {
      setLoadingFriend(null);
    }
  };

  // The create step's button: meet the selected friend, or generate from the
  // photo. Selecting on the step itself only highlights a choice.
  const confirmPick = () => {
    if (!pick) return;
    if (pick.type === 'friend') {
      const friend = PRESET_FRIENDS.find((f) => f.key === pick.key);
      if (!friend) return;
      setMetFriend(friend);
      choosePresetFriend(friend);
    } else {
      setMetFriend(null);
      handleGenerate();
    }
  };

  // A newly added photo (gallery, native camera or the in-page one) becomes
  // the selection, replacing whichever friend was highlighted
  useEffect(() => {
    if (photoPreviewUrl) setPick({ type: 'photo' });
  }, [photoPreviewUrl]);

  // `source` distinguishes the two hidden inputs ('upload' = gallery/file
  // picker, 'camera_native' = the mobile capture input) for analytics; the
  // desktop in-page camera reports itself from capturePhoto().
  const handlePhotoChange = (e, source = 'upload') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
      method: source,
      // A replaced photo usually means the first attempt was rejected or
      // disliked — worth separating from a first add.
      is_replacement: !!photoFile,
      photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
      photo_type: file.type || null,
    });
  };

  // ── "Take photo" ──────────────────────────────────────────────────────────
  // The input's `capture` attribute only opens a camera on phones/tablets —
  // desktop browsers silently ignore it and show the file picker. On desktop
  // we open an in-page getUserMedia camera instead.
  const isTouchDevice =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent)); // iPadOS reports as Mac

  const openCamera = async () => {
    if (isTouchDevice || !navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      cameraStreamRef.current = stream;
      setCameraOpen(true);
    } catch (error) {
      logger.warn('Camera unavailable, falling back to file picker:', error);
      showToast("We couldn't open your camera — choose a photo instead.", 'warning');
      fileInputRef.current?.click();
    }
  };

  const closeCamera = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    cameraStreamRef.current = null;
    setCameraOpen(false);
  }, []);

  // Attach the stream once the modal's <video> is in the DOM
  useEffect(() => {
    if (cameraOpen && cameraVideoRef.current && cameraStreamRef.current) {
      cameraVideoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraOpen]);

  // Never leave the camera running on unmount
  useEffect(() => closeCamera, [closeCamera]);

  const capturePhoto = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
        setPhotoFile(file);
        setPhotoPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(file);
        });
        funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
          method: 'camera_web',
          is_replacement: !!photoFile,
          photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
          photo_type: file.type || null,
        });
        closeCamera();
      },
      'image/jpeg',
      0.92
    );
  };

  // Hero pop-in on the meet step (spring-ish scale, then confetti)
  useEffect(() => {
    if (step !== 'meet') return undefined;
    const id = setTimeout(() => setHeroRevealed(true), 120);
    return () => clearTimeout(id);
  }, [step]);

  const handleGoogleSignIn = async () => {
    try {
      logger.log('Starting Google OAuth (funnel)...');
      setIsSigningIn(true);
      // Fired before the redirect: this is the last event PostHog gets from
      // this page load, so signin_started vs. the post-OAuth 'finishing' view
      // is what measures Google round-trip abandonment.
      funnel.trackStepCompleted('signin', { method: 'google' }, { immediate: true });
      funnel.trackImmediate(EVENTS.FUNNEL_SIGNIN_STARTED, { method: 'google' });

      // redirect_uri/state ride back in the return URL below — no need to
      // stash them. Only the funnel's own answers stay in sessionStorage so
      // they can be saved to the account after the Google round trip.
      funnelSet(FUNNEL_STEP_KEY, 'signin');
      if (childName) funnelSet(FUNNEL_NAME_KEY, childName);

      // Land straight back on /funnel-auth (not the site root) after Google,
      // carrying the funnel params in the URL so the flow resumes without
      // depending on any storage. Prefixed names avoid colliding with the
      // OAuth `state`/`code` that Supabase appends to this URL on return.
      const returnUrl = new URL('/funnel-auth', window.location.origin);
      // The intro entry picks itself back up on return (App.js FunnelAuthRoute)
      if (intro) returnUrl.searchParams.set('variant', 'intro');
      if (redirectUri) returnUrl.searchParams.set('funnel_redirect_uri', redirectUri);
      if (funnelState) returnUrl.searchParams.set('funnel_state', funnelState);
      // The ad click rides along too: iOS in-app browsers have come back from
      // Google without the stored copy (utils/adAttribution)
      const adClick = adClickReturnParam();
      if (adClick) returnUrl.searchParams.set(AD_CLICK_PARAM, adClick);

      const timeoutId = setTimeout(() => {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, { method: 'google', reason: 'timeout' });
        showToast('Sign in is taking too long. Please try again.', 'error');
      }, 15000);

      const result = await signInWithGoogle(returnUrl.toString());
      clearTimeout(timeoutId);

      if (result && result.success) {
        logger.log('Google OAuth initiated successfully');
      } else {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
          method: 'google',
          reason: 'oauth_init_failed',
          error_message: result?.error || 'unknown',
        });
        showToast(result?.error || 'Sign in failed. Please try again.', 'error');
      }
    } catch (error) {
      logger.error('Google sign in error:', error);
      setIsSigningIn(false);
      funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
        method: 'google',
        reason: 'exception',
        error_message: error.message || 'unknown',
      });
      showToast(error.message || 'Failed to sign in with Google', 'error');
    }
  };

  const stepIndex = STEP_ORDER.indexOf(
    step === 'generating' ? 'create'
      : step === 'finishing' ? 'signin'
        : CHECKOUT_STEPS.includes(step) ? 'paywall'
          : step
  );
  // Picking a friend is the first step, with nothing behind it — unless the
  // visitor came through the explainer, whose last slide is then one step back
  const backTarget = step === 'create' && explained
    ? EXPLAINER_STEPS[EXPLAINER_STEPS.length - 1]
    : BACK_TARGET[step];

  const invalidLink = !nativeCheckout && (!redirectUri || !funnelState);
  // Sign-in, the paywall and the card form are sheets rising out of the moon's
  // sky rather than cards
  // After an in-page payment the welcome stays on the paywall's card too, so
  // the sheet can drop and the character can grow into the welcome's frame
  const paidHere = step === 'success' && successEntrance === 'morph';
  const isSheet = (['signin', 'paywall', 'checkout'].includes(step) || paidHere) && !invalidLink;
  // The paywall's sheet carries the website's plan cards (TrialWall), which
  // sit side by side above phone width — give it the site's room
  const isWall = (step === 'paywall' || step === 'checkout' || paidHere) && !invalidLink;

  // ── render ────────────────────────────────────────────────────────────────
  // The paywall and the card form share one card (stage + sheet); only the
  // sheet's content changes between them (FunnelPayStage)
  const cardKey = invalidLink ? 'invalid' : step === 'checkout' || paidHere ? 'paywall' : step;

  if (opening && !invalidLink) return <FunnelWelcomeFrame />;

  return (
    <MotionConfig reducedMotion="user">
      <FunnelAuthPageStyles />
      <FunnelScreenStyles />
      <FunnelExplainerStyles />
      <FunnelCheckoutStyles />
      <div className={`fa-bg${isSheet ? ' is-sheet' : ''}`}>
        {/* Sign-in rises into the moon's sky; it's fetched a step ahead. The
            in-house checkout stays under the moon until the welcome. */}
        <FunnelSky
          scene={['signin', 'finishing', 'paywall', 'checkout'].includes(cardKey) ? 'moon' : 'night'}
          moonSoon={cardKey === 'meet' || cardKey === 'name'}
        />
        <div className={`fa-shell${isWall ? ' is-wide' : ''}`}>

          {/* Step header (segmented progress, like the iOS flow) */}
          {!invalidLink && step !== 'finishing' && step !== 'success' && (
            <motion.div
              className="fa-header"
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="fa-back-slot">
                <AnimatePresence>
                  {backTarget && (
                    <motion.button
                      className="fa-back"
                      onClick={() => {
                        funnel.track(EVENTS.FUNNEL_BACK_CLICKED, {
                          from_step: step,
                          to_step: backTarget,
                        });
                        setStep(backTarget);
                      }}
                      aria-label="Back"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
              <div className="fa-segments">
                {STEP_ORDER.map((s, i) => (
                  <div key={s} className={`fa-segment ${i <= stepIndex ? 'filled' : ''}`} />
                ))}
              </div>
            </motion.div>
          )}

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={cardKey}
              className={`fa-card fa-center ${cardKey === 'meet' ? 'fa-meet' : ''} ${isSheet ? 'fa-card-sheet' : ''} ${cardKey === 'success' ? 'fa-meet' : ''}`}
              custom={direction}
              variants={isSheet ? sheetStepVariants : stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {invalidLink && (
                <>
                  <motion.div variants={itemVariants} className="fa-panda-wrap">
                    <img className="fa-panda fa-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">Hmm, this link isn't quite right</motion.h1>
                  <motion.p variants={itemVariants} className="fa-subtitle">
                    This page is part of the Pixley sign-up flow. Head to{' '}
                    <a href="https://www.pixleyai.com">pixleyai.com</a> to get started.
                  </motion.p>
                </>
              )}

              {/* The explainer slides — only for visitors who chose "See how Pixley works" */}
              {!invalidLink && EXPLAINER_BY_KEY[step] && (
                <ExplainerSlide
                  slide={EXPLAINER_BY_KEY[step]}
                  onCreate={() => {
                    funnel.trackStepCompleted(step);
                    setStep('create');
                  }}
                  onNext={() => {
                    funnel.trackStepCompleted(step);
                    setStep(EXPLAINER_STEPS[EXPLAINER_STEPS.indexOf(step) + 1]);
                  }}
                  itemVariants={itemVariants}
                />
              )}

              {/* STEP 1 — pick a ready-made friend, or use a photo */}
              {!invalidLink && step === 'create' && (
                <FunnelFriendStep
                  friends={PRESET_FRIENDS}
                  pick={pick}
                  onPickFriend={(key) => setPick({ type: 'friend', key })}
                  onPickPhoto={() => setPick({ type: 'photo' })}
                  photoPreviewUrl={photoPreviewUrl}
                  fileInputRef={fileInputRef}
                  cameraInputRef={cameraInputRef}
                  onPhotoChange={handlePhotoChange}
                  onOpenCamera={openCamera}
                  loadingFriend={loadingFriend}
                  onConfirm={confirmPick}
                  itemVariants={itemVariants}
                />
              )}

              {/* A Google-ad visit's terms, under the first screen's button
                  (picking a friend, or the intro entry's first slide) */}
              {!invalidLink && googleLanding && (step === 'create' || step === EXPLAINER_STEPS[0]) && (
                <motion.p variants={itemVariants} className="fa-google-terms" data-testid="funnel-google-terms">
                  {landingTerms && <span>{landingTerms}</span>}
                  <span className="fa-google-terms-safety">{GOOGLE_LANDING_SAFETY}</span>
                </motion.p>
              )}

              {/* STEP 1b — generating (NOCharacterGenerationStep) */}
              {!invalidLink && step === 'generating' && (
                <>
                  <motion.div variants={itemVariants}>
                    <div className="fa-panda-glow">
                      <LoadingPandaVideo className="fa-panda-video" />
                    </div>
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fa-title">Bringing them to life…</motion.h1>
                  <motion.div variants={itemVariants} className="fa-gen-msg">
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={genMsgIndex}
                        className="fa-subtitle"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {GEN_MESSAGES[genMsgIndex]}
                      </motion.p>
                    </AnimatePresence>
                  </motion.div>
                  <motion.div variants={itemVariants} className="fa-progress-row">
                    <div className="fa-progress-track">
                      <div className="fa-progress-fill" style={{ width: `${Math.round(genProgress * 100)}%` }}>
                        <div className="fa-progress-shimmer" />
                      </div>
                    </div>
                    <span className="fa-progress-label">{Math.round(genProgress * 100)}%</span>
                  </motion.div>

                  {/* The pitch, while they have a moment: three slides on
                      characters, stories and storybooks (FunnelWhyPixley) */}
                  <motion.div variants={itemVariants}>
                    <FunnelWhyPixley />
                  </motion.div>

                  {/* The one open-ended wait in the funnel — offer something to
                      watch rather than just the bar. Plays in an overlay, so the
                      generation request in flight here is never abandoned. */}
                  <FunnelWaitVideo
                    onPlay={(episode) =>
                      funnel.track(EVENTS.FUNNEL_WAIT_VIDEO_PLAYED, {
                        episode_id: episode?.id || null,
                        episode_name: episode?.name || null,
                        // Where in the wait they gave up on the bar
                        progress_percent: Math.round(genProgress * 100),
                      })
                    }
                  />
                </>
              )}

              {/* STEP 2 — meet the character (NOMeetPixleyStep) */}
              {!invalidLink && step === 'meet' && previewImage && (
                <>
                  {heroRevealed && <Confetti />}
                  <FunnelMeetStep
                    friend={metFriend}
                    previewImage={previewImage}
                    heroRevealed={heroRevealed}
                    onSave={() => {
                      funnel.trackStepCompleted('meet');
                      setStep('name');
                    }}
                    itemVariants={itemVariants}
                  />
                </>
              )}

              {/* STEP 3 — name (NOChildNameStep), once they've seen their character */}
              {!invalidLink && step === 'name' && (
                <FunnelNameStep
                  childName={childName}
                  onChangeName={(value) => {
                    setChildName(value);
                    funnelSet(FUNNEL_NAME_KEY, value);
                  }}
                  onSubmit={submitName}
                  itemVariants={itemVariants}
                />
              )}

              {/* STEP 4 — sign in (NOSignInStep) */}
              {!invalidLink && step === 'signin' && (
                <FunnelSigninStep
                  childName={childName}
                  friend={metFriend}
                  previewImage={previewImage}
                  isSigningIn={isSigningIn}
                  onGoogleSignIn={handleGoogleSignIn}
                  itemVariants={itemVariants}
                />
              )}

              {/* Finishing — saving character + returning to the funnel */}
              {!invalidLink && step === 'finishing' && (
                showSuccess ? (
                  <>
                    <div className="fa-success-check"><CheckIcon size={42} /></div>
                    <h1 className="fa-title">You're all set!</h1>
                    <p className="fa-subtitle">Taking you back to finish signing up…</p>
                  </>
                ) : (
                  <>
                    <motion.div variants={itemVariants}>
                      <div className="fa-panda-glow">
                        <LoadingPandaVideo className="fa-panda-video" />
                      </div>
                    </motion.div>
                    <motion.h1 variants={itemVariants} className="fa-title">Setting up your account…</motion.h1>
                    <motion.p variants={itemVariants} className="fa-subtitle">
                      {childName.trim() ? `Saving ${childName.trim()}'s character to your account.` : 'Just a moment.'}
                    </motion.p>
                  </>
                )
              )}

              {/* In-house checkout — the paywall, then Stripe's card form, on
                  one stage: the character and the sheet stay, the content slides */}
              {!invalidLink && (step === 'paywall' || step === 'checkout' || paidHere) && (
                <FunnelPayStage
                  step={step}
                  friend={metFriend}
                  previewImage={previewImage}
                  childName={childName}
                  success={paidHere ? (
                    <FunnelSuccessStep
                      childName={childName}
                      friend={metFriend}
                      previewImage={previewImage}
                      offer={offer}
                      activation={activation}
                      onContinue={finishToApp}
                      onDownloadApp={() => funnel.trackImmediate(EVENTS.FUNNEL_APP_DOWNLOAD_CLICKED, { activation })}
                      itemVariants={itemVariants}
                      entrance="morph"
                    />
                  ) : null}
                >
                  {step === 'paywall' && (
                    <FunnelPaywallStep
                      childName={childName}
                      previewImage={previewImage}
                      plansData={plansData}
                      billing={plan.billing}
                      onSelectBilling={selectBilling}
                      onCheckout={startCheckout}
                      busy={checkoutBusy}
                      error={checkoutError}
                      promo={promo}
                      loadError={plansError}
                      onRetry={retryPlans}
                      offerVariant={offerVariant}
                      invitation={payLinkReady}
                      chosenCents={plan.chosenCents}
                      onChooseCents={choosePrice}
                      onPriceChooserOpened={priceChooserOpened}
                      itemVariants={itemVariants}
                    />
                  )}
                  {step === 'checkout' && checkout && (
                    <FunnelCheckoutStep
                      childName={childName}
                      friend={metFriend}
                      previewImage={previewImage}
                      offer={checkout.offer || offer}
                      notice={promoNotice}
                      clientSecret={checkout.clientSecret}
                      publishableKey={checkout.publishableKey}
                      onComplete={onCheckoutComplete}
                      onUnavailable={startHostedCheckout}
                      onChangePlan={() => {
                        funnel.track(EVENTS.FUNNEL_BACK_CLICKED, { from_step: 'checkout', to_step: 'paywall' });
                        setCheckout(null);
                        setStep('paywall');
                      }}
                      itemVariants={itemVariants}
                    />
                  )}
                  {step === 'checkout' && !checkout && (
                    // A reload mid-payment loses the session (the secret is never
                    // stored); back to the paywall for a fresh one
                    <div className="fc-sheet-content fa-center">
                      <motion.div variants={itemVariants}>
                        <div className="fa-panda-glow"><LoadingPandaVideo className="fa-panda-video" /></div>
                      </motion.div>
                      <motion.h1 variants={itemVariants} className="fa-title">One moment…</motion.h1>
                      <motion.div variants={itemVariants}>
                        <button className="fa-primary-btn" onClick={() => setStep('paywall')}>
                          Back to plans <span className="fa-chevron">›</span>
                        </button>
                      </motion.div>
                    </div>
                  )}
                </FunnelPayStage>
              )}

              {/* …and the welcome when success was reached without the stage
                  (the hosted-Stripe return, a reload) */}
              {!invalidLink && step === 'success' && !paidHere && (
                <>
                  <Confetti />
                  <FunnelSuccessStep
                    childName={childName}
                    friend={metFriend}
                    previewImage={previewImage}
                    offer={offer}
                    activation={activation}
                    onContinue={finishToApp}
                    onDownloadApp={() => funnel.trackImmediate(EVENTS.FUNNEL_APP_DOWNLOAD_CLICKED, { activation })}
                    itemVariants={itemVariants}
                  />
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Sign-in's sheet carries its own wordmark, at the foot of the screen */}
          {!isSheet && (
            <motion.p
              className="fa-footer-note"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              PIXLEY
            </motion.p>
          )}

        </div>

        {/* In-page camera (desktop, where the capture attribute is ignored) */}
        <AnimatePresence>
          {cameraOpen && (
            <motion.div
              className="fa-camera-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                className="fa-camera-modal"
                initial={{ opacity: 0, scale: 0.92, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 8 }}
                transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              >
                <video ref={cameraVideoRef} className="fa-camera-video" autoPlay playsInline webkit-playsinline="" muted />
                <div className="fa-camera-actions">
                  <button className="fa-camera-cancel" onClick={closeCamera}>Cancel</button>
                  <button className="fa-camera-shutter" onClick={capturePhoto} aria-label="Take photo" />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Existing account — welcome back, then off to the main site */}
        <AnimatePresence>
          {showExistingAccount && (
            <motion.div
              className="fa-exist-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                className="fa-exist-modal"
                role="dialog"
                aria-modal="true"
                aria-label={showExistingAccount === 'member' ? "You're already a member" : "You already have an account"}
                initial={{ opacity: 0, scale: 0.82, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              >
                <span className="fa-exist-glow" aria-hidden="true" />
                <motion.img
                  className="fa-panda"
                  src="/assets/pixley-panda-nobg.png"
                  alt="Pixley Panda"
                  initial={{ scale: 0, rotate: -14 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 15, delay: 0.15 }}
                />
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="fa-eyebrow">Welcome back</div>
                  {showExistingAccount === 'member' ? (
                    <>
                      <h1 className="fa-title">You're already a member!</h1>
                      <p className="fa-subtitle">Your Pixley membership is active, so there's nothing to pay — we're taking you back to Pixley.</p>
                    </>
                  ) : (
                    <>
                      <h1 className="fa-title">You already have an account!</h1>
                      <p className="fa-subtitle">No need to sign up again — we're taking you back to Pixley.</p>
                    </>
                  )}
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  <div className="fa-exist-progress" aria-hidden="true"><div /></div>
                  <button
                    className="fa-primary-btn"
                    onClick={() => window.location.replace('https://www.pixleyai.com')}
                  >
                    Take me there now <span className="fa-chevron">›</span>
                  </button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
};

// iOS onboarding theme, translated from NewOnboardingKit.swift:
// bg #F5FAFF→#E8F1FF, ink #0B1A38, subtitle #6C7A8F, accent #0A6CFF,
// card stroke #E8EDF5, track #DDE6F2, rounded Fredoka titles.
const FunnelAuthPageStyles = () => (
  <style>{`
    .fa-bg {
      position: relative;
      min-height: 100vh;
      min-height: 100dvh;
      /* The night sky's own colours, under FunnelSky until its poster paints */
      background: linear-gradient(180deg, #243EA0 0%, #637BE9 50%, #BFABF1 100%);
      font-family: 'Inter', 'Fredoka', sans-serif;
      display: flex;
      padding: calc(32px + env(safe-area-inset-top, 0px))
               calc(16px + env(safe-area-inset-right, 0px))
               calc(32px + env(safe-area-inset-bottom, 0px))
               calc(16px + env(safe-area-inset-left, 0px));
      box-sizing: border-box;
    }

    /* Touch polish — no gray tap flash, no double-tap zoom delay */
    .fa-primary-btn, .fa-back,
    .fa-camera-shutter, .fa-camera-cancel {
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }

    /* The shell spans the screen's height with the header pinned to the top,
       so the back button and progress bar sit at the same height on every
       step. Each card centres itself in the room below, the wordmark at the
       foot; the margins degrade to plain scrolling when a card is taller than
       the screen. */
    .fa-shell {
      width: 100%; max-width: 520px;
      margin: 0 auto;
      align-self: stretch;
      display: flex; flex-direction: column;
      position: relative; z-index: 1;
    }
    .fa-shell > .fa-card { margin: auto 0; }
    .fa-shell.is-wide { max-width: 700px; }

    /* Sign-in (FunnelSigninStep): no card. The sheet rises out of the bottom
       of the screen, edge to edge on phones. */
    .fa-bg.is-sheet { padding-bottom: 0; }
    .fa-shell > .fa-card.fa-card-sheet {
      flex: 1;
      margin: 0;
      display: flex; flex-direction: column;
      padding: 0;
      background: none;
      box-shadow: none;
      border-radius: 0;
      overflow: visible;
    }
    @media (max-width: 560px) {
      .fa-card-sheet .fs-signin-sheet {
        margin-left: calc(-16px - env(safe-area-inset-left, 0px));
        margin-right: calc(-16px - env(safe-area-inset-right, 0px));
      }
    }
    @media (max-width: 480px) {
      .fa-card-sheet .fs-signin-sheet {
        margin-left: calc(-12px - env(safe-area-inset-left, 0px));
        margin-right: calc(-12px - env(safe-area-inset-right, 0px));
      }
    }

    /* Step header */
    .fa-header {
      display: flex;
      align-items: center;
      gap: 14px;
      margin: 0 4px 22px;
    }
    .fa-back-slot {
      width: 44px; height: 44px; flex-shrink: 0;
      transition: width 0.35s cubic-bezier(.22,1,.36,1);
    }
    /* No back button (first step, reveal): the progress bar stretches into its
       place, but the header keeps its height, so the bar stays level */
    .fa-back-slot:empty { width: 0; }
    .fa-back {
      width: 44px; height: 44px;
      display: flex; align-items: center; justify-content: center;
      background: #FFF;
      border: none;
      border-radius: 14px;
      color: #162642;
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(40,62,128,0.10);
      transition: box-shadow 0.2s, color 0.2s;
    }
    .fa-back:hover {
      box-shadow: 0 6px 18px rgba(41,111,251,0.18);
      color: #296FFB;
    }
    .fa-segments { display: flex; gap: 8px; flex: 1; }
    .fa-segment {
      height: 6px; flex: 1; border-radius: 3px;
      background: rgba(255,255,255,0.3);
      overflow: hidden;
    }
    .fa-segment::after {
      content: '';
      display: block;
      height: 100%; width: 0;
      border-radius: 3px;
      background: #FFF;
      transition: width 0.6s cubic-bezier(.22,1,.36,1);
    }
    .fa-segment.filled::after { width: 100%; }

    /* Card. Opaque, and deliberately no backdrop-filter: that would make the
       card the containing block for position:fixed descendants, trapping the
       wait video's full-screen overlay (FunnelWaitVideo) inside it. */
    .fa-card {
      position: relative;
      background: #FFF;
      border-radius: 32px;
      box-shadow: 0 24px 60px rgba(14,24,84,0.26), 0 2px 8px rgba(14,24,84,0.10);
      padding: 34px 26px 30px;
      overflow: hidden;
    }
    .fa-center { text-align: center; }

    .fa-footer-note {
      text-align: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 15px; font-weight: 700;
      color: rgba(255,255,255,0.88);
      text-shadow: 0 1px 10px rgba(36,48,140,0.35);
      letter-spacing: 5px;
      margin: 22px 0 0;
    }

    /* Type — same values as the shared .fs-* screens (FunnelScreens.js), for
       the steps that are this page's own (waits, invalid link, welcome back) */
    .fa-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 700;
      letter-spacing: 0.15em; text-transform: uppercase;
      color: #2869FC; margin-bottom: 12px;
    }
    .fa-title {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 32px; font-weight: 700;
      color: #162642; line-height: 1.1;
      letter-spacing: -0.02em;
      margin: 0 0 12px;
      text-wrap: balance;
    }
    .fa-subtitle {
      font-size: 16.5px; color: #6B7892; line-height: 1.5;
      margin: 0 0 22px;
      text-wrap: pretty;
    }
    .fa-subtitle a { color: #296FFB; font-weight: 600; }

    /* A Google-ad visit's price and safety lines: small print under the
       first screen's button */
    .fa-google-terms {
      margin: 16px 0 0;
      font-size: 13px; line-height: 1.45;
      color: #6B7892;
      text-align: center;
    }
    .fa-google-terms span { display: block; }
    .fa-google-terms-safety { margin-top: 2px; color: #8A96AD; }

    /* Panda mascot */
    .fa-panda-wrap { display: flex; justify-content: center; }
    .fa-panda {
      width: 118px; height: 118px;
      object-fit: contain;
      margin-bottom: 10px;
      filter: drop-shadow(0 10px 18px rgba(11,26,51,0.14));
    }
    .fa-float { animation: faFloat 2.6s ease-in-out infinite; }
    @keyframes faFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-7px); }
    }
    .fa-panda-glow {
      width: 124px; height: 124px; margin: 0 auto 6px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.16) 0%, rgba(10,108,255,0) 70%);
      display: flex; align-items: center; justify-content: center;
      animation: faPulse 1.9s ease-in-out infinite;
    }
    @keyframes faPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .fa-panda-video { width: 176px; height: 99px; max-width: none; object-fit: contain; }

    /* Buttons — primary matches .fs-cta */
    .fa-primary-btn {
      position: relative;
      width: 100%;
      min-height: 58px;
      display: flex; align-items: center; justify-content: center; gap: 10px;
      padding: 14px 22px;
      background: linear-gradient(90deg, #296FFB 0%, #3E8DFD 100%);
      color: #FFF;
      font-size: 20px; font-weight: 700;
      font-family: 'Fredoka', 'Inter', sans-serif;
      border: none; border-radius: 20px;
      cursor: pointer;
      box-shadow: 0 12px 26px rgba(41,111,251,0.26), inset 0 1px 0 rgba(255,255,255,0.22);
      transition: transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms, opacity 0.2s;
    }
    .fa-primary-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 16px 32px rgba(41,111,251,0.34), inset 0 1px 0 rgba(255,255,255,0.22);
    }
    .fa-primary-btn:active:not(:disabled) { transform: scale(0.98); }
    .fa-primary-btn:disabled { opacity: 0.45; cursor: default; box-shadow: none; }
    .fa-chevron { font-size: 22px; line-height: 1; transition: transform 0.2s ease; }
    .fa-primary-btn:hover:not(:disabled) .fa-chevron { transform: translateX(3px); }

    .fa-primary-btn:focus-visible, .fa-back:focus-visible {
      outline: 3px solid rgba(41,111,251,0.4);
      outline-offset: 2px;
    }

    /* In-page camera modal (desktop fallback for the capture attribute) */
    .fa-camera-overlay {
      position: fixed; inset: 0; z-index: 60;
      background: rgba(11,26,51,0.72);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fa-camera-modal {
      width: min(560px, 100%);
      background: #FFF;
      border-radius: 24px;
      padding: 16px;
      box-shadow: 0 24px 60px rgba(11,26,51,0.35);
    }
    .fa-camera-video {
      width: 100%;
      border-radius: 16px;
      transform: scaleX(-1); /* mirror the live preview like a selfie cam */
      background: #0B1A38;
    }
    .fa-camera-actions {
      position: relative;
      display: flex; align-items: center; justify-content: center;
      padding-top: 14px;
    }
    .fa-camera-cancel {
      position: absolute; left: 8px;
      background: none; border: none;
      font-family: 'Inter', sans-serif;
      font-size: 15px; font-weight: 600; color: #6C7A8F;
      cursor: pointer;
      transition: color 0.2s;
    }
    .fa-camera-cancel:hover { color: #0B1A38; }
    .fa-camera-shutter {
      width: 58px; height: 58px;
      border-radius: 50%;
      background: #0A6CFF;
      border: 4px solid #FFF;
      box-shadow: 0 0 0 3px #0A6CFF;
      cursor: pointer;
      transition: transform 0.15s cubic-bezier(.2,.8,.2,1);
    }
    .fa-camera-shutter:hover { transform: scale(1.08); }
    .fa-camera-shutter:active { transform: scale(0.92); }

    /* Existing-account modal */
    .fa-exist-overlay {
      position: fixed; inset: 0; z-index: 70;
      background: rgba(11,26,51,0.55);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fa-exist-modal {
      position: relative;
      width: min(430px, 100%);
      background: #FFF;
      border-radius: 28px;
      padding: 36px 28px 28px;
      text-align: center;
      overflow: hidden;
      box-shadow: 0 30px 80px rgba(11,26,51,0.35);
    }
    .fa-exist-glow {
      position: absolute; top: -70px; left: 50%;
      width: 260px; height: 260px;
      transform: translateX(-50%);
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.14) 0%, rgba(10,108,255,0) 70%);
      pointer-events: none;
    }
    .fa-exist-modal .fa-panda { display: block; margin: 0 auto 10px; }
    .fa-exist-modal .fa-title { font-size: 26px; }
    .fa-exist-modal .fa-subtitle { margin-bottom: 20px; }
    .fa-exist-progress {
      height: 6px;
      background: #E8EDF5;
      border-radius: 3px;
      overflow: hidden;
      margin: 0 0 18px;
    }
    .fa-exist-progress div {
      height: 100%;
      width: 0;
      border-radius: 3px;
      background: linear-gradient(90deg, #0A6CFF, #7C5CFF);
      animation: faExistCountdown 3.4s linear forwards;
    }
    @keyframes faExistCountdown { to { width: 100%; } }

    /* Progress bar (NOProgressBar with shimmer) */
    .fa-gen-msg {
      min-height: 40px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 12px;
    }
    .fa-gen-msg .fa-subtitle { margin: 0; }
    .fa-progress-row { display: flex; align-items: center; gap: 12px; }
    .fa-progress-track {
      flex: 1; height: 12px;
      background: #DDE6F2;
      border-radius: 6px;
      overflow: hidden;
    }
    .fa-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #296FFB, #3E8DFD);
      border-radius: 6px;
      position: relative;
      overflow: hidden;
      transition: width 0.5s ease-in-out;
    }
    .fa-progress-shimmer {
      position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
      animation: faShimmer 1.4s linear infinite;
    }
    @keyframes faShimmer {
      from { transform: translateX(-100%); }
      to { transform: translateX(100%); }
    }
    .fa-progress-label {
      font-size: 15px; font-weight: 700; color: #0B1A38;
      font-variant-numeric: tabular-nums;
      min-width: 44px; text-align: right;
    }

    /* Meet step — the confetti falls past the card edge */
    .fa-meet { overflow: visible; }

    /* Confetti */
    .fa-confetti {
      position: absolute; inset: 0;
      pointer-events: none;
      overflow: hidden;
      border-radius: 32px;
      z-index: 1;
    }
    .fa-confetti span {
      position: absolute;
      top: -20px;
      width: 11px; height: 11px;
      border-radius: 2px;
      animation-name: faConfettiFall;
      animation-timing-function: ease-in;
      animation-fill-mode: forwards;
    }
    @keyframes faConfettiFall {
      to {
        transform: translate(var(--drift), 640px) rotate(var(--spin));
        opacity: 0;
      }
    }

    /* Success */
    .fa-success-check {
      position: relative;
      width: 80px; height: 80px;
      margin: 0 auto 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3EDC8F, #20A96A);
      color: #FFF;
      font-size: 34px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 10px 26px rgba(32,169,106,0.35);
      animation: faSuccessPop 0.6s ease-out;
    }
    .fa-success-check::after {
      content: '';
      position: absolute; inset: 0;
      border-radius: 50%;
      border: 3px solid rgba(62,220,143,0.5);
      animation: faRipple 1.4s ease-out infinite;
    }
    @keyframes faSuccessPop {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes faRipple {
      from { transform: scale(1); opacity: 1; }
      to { transform: scale(1.7); opacity: 0; }
    }

    /* Mobile */
    @media (max-width: 480px) {
      .fa-bg {
        padding: calc(18px + env(safe-area-inset-top, 0px))
                 calc(12px + env(safe-area-inset-right, 0px))
                 calc(20px + env(safe-area-inset-bottom, 0px))
                 calc(12px + env(safe-area-inset-left, 0px));
      }
      .fa-card { padding: 26px 18px 24px; border-radius: 30px; }
      .fa-confetti { border-radius: 30px; }
      .fa-title { font-size: 29px; }
      .fa-subtitle { font-size: 16px; }
      .fa-panda { width: 100px; height: 100px; }
      .fa-header { gap: 12px; margin-bottom: 18px; }
      /* The paywall fits one phone screen: the header sits closer to it */
      .fa-shell.is-wide .fa-header { margin-bottom: 10px; }

      .fa-primary-btn { font-size: 19px; }
      .fa-footer-note { font-size: 13px; margin-top: 16px; }
    }

    /* Small phones (iPhone SE, compact Androids) */
    @media (max-width: 374px) {
      .fa-card { padding: 22px 14px 20px; }
      .fa-title { font-size: 26px; }
      .fa-panda { width: 88px; height: 88px; }
    }

    /* Short viewports (landscape phones, small screens with keyboard up):
       compress the tall decorative pieces so the card fits without scroll */
    @media (max-height: 640px) {
      .fa-bg { padding-top: 14px; padding-bottom: 14px; }
      .fa-header { margin-bottom: 12px; }
      .fa-card { padding: 22px 20px 20px; }
      .fa-title { font-size: 26px; }
      .fa-subtitle { margin-bottom: 16px; }
      .fa-panda { width: 78px; height: 78px; margin-bottom: 6px; }
      .fa-panda-glow { width: 100px; height: 100px; margin-bottom: 4px; }
      .fa-panda-video { width: 150px; height: 84px; }
      .fa-gen-msg { min-height: 36px; margin-bottom: 10px; }
      .fa-footer-note { display: none; }
    }

    /* Touch devices — bigger camera shutter for thumbs */
    @media (pointer: coarse) {
      .fa-camera-shutter { width: 66px; height: 66px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fa-float, .fa-panda-glow, .fa-progress-shimmer,
      .fa-success-check, .fa-success-check::after { animation: none; }
      .fa-primary-btn:hover:not(:disabled) { transform: none; }
      .fa-confetti { display: none; }
    }
  `}</style>
);

export default FunnelAuthPage;
